/*
 * AI 填表员首页 · V3(双列工作台版)
 *
 * 严格按设计稿(AI填表员.pen 节点 BdhoJ)实现:
 *   顶栏 + 左「AI 引导对话面板」(460 宽) + 右(上传 dropzone + 我的模板 + 最近文档)。
 * 区别于 V2 单页流式 Hero:V3 把首页做成「进站即工作台」,降低上传/发起的心理门槛。
 *
 * 上传 / 积分拦截 / 生成 / 模板发起逻辑完全复用 V2(逐字搬运)。按红队结论 V3 自实现
 * 状态机、不抽 hook,避免改动 V1/V2 生产流量主体。视图层重写为双列。
 *
 * 入口三态由 aiClerk/index.tsx 路由决定:默认 V3,?v=1 → 旧 Home,?v=2 → HomeV2。
 */

import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { message, Button, Modal, Popover, Input } from 'antd'
// chip 点击后先弹时间选择,选完再拼进 message,独立自包含组件
import ChipTimePicker, { TimeRange } from '../components/ChipTimePicker'
import {
  AlertOutlined,
  ArrowUpOutlined,
  AuditOutlined,
  CameraOutlined,
  CarOutlined,
  CheckCircleFilled,
  CheckCircleOutlined,
  CheckOutlined,
  CloseCircleOutlined,
  CloseOutlined,
  CloudUploadOutlined,
  ContainerOutlined,
  DatabaseOutlined,
  DeleteOutlined,
  DiffOutlined,
  DownloadOutlined,
  DownOutlined,
  EditOutlined,
  ExceptionOutlined,
  ExportOutlined,
  FileDoneOutlined,
  FileExcelFilled,
  FileFilled,
  FilePdfFilled,
  FilePptFilled,
  FileProtectOutlined,
  FileTextFilled,
  FileTextOutlined,
  FileWordFilled,
  FireOutlined,
  FlagOutlined,
  FormOutlined,
  FundOutlined,
  GiftFilled,
  HighlightOutlined,
  InboxOutlined,
  LoadingOutlined,
  MessageOutlined,
  NotificationOutlined,
  PaperClipOutlined,
  PictureOutlined,
  PlayCircleOutlined,
  ProfileOutlined,
  ProjectOutlined,
  ReadOutlined,
  ReconciliationOutlined,
  SafetyCertificateOutlined,
  SafetyOutlined,
  ScanOutlined,
  ScheduleOutlined,
  SnippetsOutlined,
  SolutionOutlined,
  StarFilled,
  SwapOutlined,
  TableOutlined,
  TeamOutlined,
  ThunderboltFilled,
  ToolOutlined,
  UploadOutlined,
  UserOutlined,
  WarningOutlined
} from '@ant-design/icons'
import { xheyAi, SINGLE_QUERY_COST } from '../api'
import type { CreditsBalance } from '../api'
import { useCurrentFile } from '../ctx/FileCtx'
import { resolveQuickChips } from './industryChips'
import { getChipTaskName } from './chipTaskName'
import { getChipPreviewUrl } from './chipPreview'
import {
  computeExcelDiff,
  diffToPromptText,
  readFileAsArrayBuffer,
  probeTemplateSampleMatch,
  MatchProbeResult
} from '../utils/excelDiff'
import { ensureSafeFile } from '../utils/sanitizeFileName'
import {
  OFFICE_ACCEPT_TYPES as ACCEPT_TYPES,
  COMPOSER_ACCEPT_TYPES,
  validateOfficeFile,
  validateComposerFile,
  classifyFile,
  getFileExt,
  resolveMediaType
} from '../utils/officeFileTypes'
import {
  trackEnter,
  trackClick,
  trackResult,
  trackDialogShow,
  trackDialogClick
} from '../utils/track'
import { useCredits } from '../ctx/CreditsCtx'
import {
  usePayment,
  useVipKind,
  useAppSelector,
  useAppDispatch
} from '../../../app/hooks'
import { setPaymentVisible } from '../../../actions/main'
import { useGroupPhotoNumFormated } from '../../../app/hooks/useGroupPhotoNumFormated'
import CreditsExhaustedModal from '../components/CreditsExhaustedModal'
import PointsPackModal from '../../components/PointsPackModal'
import PointsEntryChip from '../../components/PointsEntryChip'
import PointsStatusCard from '../../components/PointsStatusCard'
import { aiClerkPayContent } from '../utils/paymentContent'
import DemoReplayModal from '../demo/DemoReplayModal'
import { EditSkillModal } from '../components/EditSkillModal'
import CONFIG from '../../../constants/config'
import type { SkillItem, FileItem } from '../types'
// sessionStorage key 与 SamplePayload 复用旧 Home 的定义(同一份,避免 key 漂移导致
// work 页消费不到)
import {
  SAMPLE_DIFF_PROMPT_KEY,
  DESCRIBE_PROMPT_KEY,
  DESCRIBE_ATTACHMENTS_KEY
} from './index'
import type { SamplePayload } from './index'
// 发起框图片附件的状态机类型(与 work 页 ChatPanel composer 同一份,避免结构漂移)
import type { ComposerAttachment } from '../components/ChatPanel'
import './index.scss'
import './HomeV3.scss'
import HeroCarousel from './guide/HeroCarousel'
import dataSamplePhoto from './guide/data-sample.jpg'
import SupportedTypes from './guide/SupportedTypes'
import GiftModal from './guide/GiftModal'

function formatBytes(n?: number): string {
  if (!n || n < 0) return ''
  if (n < 1024) return `${n} B`
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`
  if (n < 1024 * 1024 * 1024) return `${(n / 1024 / 1024).toFixed(1)} MB`
  return `${(n / 1024 / 1024 / 1024).toFixed(2)} GB`
}

function formatSessionTime(input: string | number | undefined): string {
  if (!input) return ''
  const ts = typeof input === 'number' ? input : new Date(input).getTime()
  if (Number.isNaN(ts)) return ''
  const diff = Date.now() - ts
  const mins = Math.floor(diff / 60000)
  if (mins < 1) return '刚刚'
  if (mins < 60) return `${mins} 分钟前`
  const hours = Math.floor(mins / 60)
  if (hours < 24) return `${hours} 小时前`
  const days = Math.floor(hours / 24)
  if (days < 7) return `${days} 天前`
  const d = new Date(ts)
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
}

// 文件类型 → antd6 实心图标查表（pickFileIconStyle 返回的 type 字符串）
const FILE_FILLED_ICONS: Record<string, React.ComponentType<any>> = {
  'file-excel': FileExcelFilled,
  'file-word': FileWordFilled,
  'file-ppt': FilePptFilled,
  'file-pdf': FilePdfFilled,
  'file-text': FileTextFilled,
  file: FileFilled
}
const FileTypeIcon = ({
  name,
  ...rest
}: {
  name: string
  [k: string]: any
}) => {
  const C = FILE_FILLED_ICONS[name]
  return C ? <C {...rest} /> : null
}

function pickFileIconStyle(filename: string): {
  type:
    | 'file-excel'
    | 'file-word'
    | 'file-pdf'
    | 'file-ppt'
    | 'file-text'
    | 'file'
  bg: string
  color: string
} {
  const ext = (filename.split('.').pop() || '').toLowerCase()
  if (ext === 'xlsx' || ext === 'xls' || ext === 'csv')
    return { type: 'file-excel', bg: '#ECFDF5', color: '#047857' }
  if (ext === 'docx' || ext === 'doc')
    return { type: 'file-word', bg: '#EFF6FF', color: '#1D4ED8' }
  if (ext === 'pdf')
    return { type: 'file-pdf', bg: '#FFEBEE', color: '#C62828' }
  if (ext === 'pptx' || ext === 'ppt')
    return { type: 'file-ppt', bg: '#FFF3E0', color: '#D84315' }
  if (ext === 'txt' || ext === 'md')
    return { type: 'file-text', bg: '#F5F5F5', color: '#616161' }
  return { type: 'file', bg: '#F5F5F5', color: '#757575' }
}

const XLSX_MEDIA_TYPE =
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
const XLS_MEDIA_TYPE = 'application/vnd.ms-excel'
const DOCX_MEDIA_TYPE =
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
const DOC_MEDIA_TYPE = 'application/msword'

function pickMediaType(filename: string): string {
  const ext = (filename.split('.').pop() || '').toLowerCase()
  if (ext === 'xlsx') return XLSX_MEDIA_TYPE
  if (ext === 'xls') return XLS_MEDIA_TYPE
  if (ext === 'docx') return DOCX_MEDIA_TYPE
  if (ext === 'doc') return DOC_MEDIA_TYPE
  return 'application/octet-stream'
}

const TRUST_NAMES = [
  '建工',
  '中铁',
  '华建',
  '天恒',
  '中联',
  '大成',
  '宏远',
  '鑫源',
  '恒通',
  '亿达'
]
const TRUST_COLORS = ['#6E3AF2', '#2E6BE6', '#16A06E', '#D9870B', '#E07B2A']

/**
 * chip 图标名(源老栈用 antd3 `<Icon type="xxx" />` 的字符串) → antd6 Outlined 组件映射表。
 * 重构版删了 LegacyIcon,动态图标改成按名字查表拿组件渲染。三套 chip 里出现过的图标名
 * 都要在此有对应项,查不到会渲染空白。
 */
const CHIP_ICON_MAP: Record<string, React.ComponentType<any>> = {
  profile: ProfileOutlined,
  schedule: ScheduleOutlined,
  team: TeamOutlined,
  warning: WarningOutlined,
  safety: SafetyOutlined,
  'file-protect': FileProtectOutlined,
  alert: AlertOutlined,
  exception: ExceptionOutlined,
  container: ContainerOutlined,
  'file-text': FileTextOutlined,
  project: ProjectOutlined,
  solution: SolutionOutlined,
  camera: CameraOutlined,
  snippets: SnippetsOutlined,
  inbox: InboxOutlined,
  tool: ToolOutlined,
  audit: AuditOutlined,
  read: ReadOutlined,
  fire: FireOutlined,
  'safety-certificate': SafetyCertificateOutlined,
  flag: FlagOutlined,
  fund: FundOutlined,
  diff: DiffOutlined,
  notification: NotificationOutlined,
  table: TableOutlined,
  'file-done': FileDoneOutlined,
  reconciliation: ReconciliationOutlined,
  car: CarOutlined,
  form: FormOutlined
}

/** 按 chip.icon 名查表渲染 Outlined 图标;查不到回落到 ProfileOutlined,避免空白 */
const ChipIcon = ({ name, ...rest }: { name: string; [k: string]: any }) => {
  const C = CHIP_ICON_MAP[name] || ProfileOutlined
  return <C {...rest} />
}

/**
 * 左栏「快速开始」chip 数据结构。
 * label:展示在 chip 上的短标题;message:点击后实际发送给 AI 的完整描述。
 * 拆成两个字段的原因:安全 / 拼图场景的描述较长,直接铺在 chip 上会撑爆按钮。
 */
interface QuickChip {
  icon: string
  label: string
  message: string
  /** v4 融入 AI 能力(条码/图像识别/隐患识别/质检)的 chip,需 SVIP 及以上才能用。
   *  点击时前端拦截 + 弹 SVIP 升级弹窗。 */
  svipOnly?: boolean
}

/** chip 点击时拉哪种预生成模板:照片档案/图册/影像类用 word(图文卡片版更贴合),
 *  其余台账/记录/验收/考勤用 excel。关键词与模板渲染引擎的卡片判定保持同一套,
 *  保证「入口名 → 模板类型」两端一致。 */
function chipDocType(label: string): 'excel' | 'word' {
  return /影像|照片档案|照片汇总|照片台账|归档|拼图|图集|图册|相册/.test(label)
    ? 'word'
    : 'excel'
}

/**
 * 时间范围预设 · chip 点击后先弹时间选择,选完再发送第一条消息
 *
 * 目的:模板本身描述的是"做一份 XX 表"(如"施工验收台账"),但用户到底想统计**哪段时间**
 * 是隐含维度,当前 chip 走"本月"隐式默认,AI 需要在对话里回问。前置一层选择可以:
 *   1) 提前定死时间段,一条消息就带够信息,减少一轮反问
 *   2) 让用户对"我要生成什么范围的表"有掌控感
 *
 * 组件已抽到 ../components/ChipTimePicker(自包含 CSS,不再借 UserAskBlock/Sheet 的类)。
 */

// ════════ 精修版(右栏介绍区)数据,严格按设计稿 r9Wq8c 文案 ════════

/** 照片自带的字段(数据映射:照片 → 表格的左侧来源) */
const PF_PHOTO_FIELDS = [
  '拍摄时间',
  '经纬度',
  '真实海拔',
  '天气',
  '作业人员',
  '水印名称'
]

/** 台账表头(数据映射:照片 → 表格的右侧目标) */
const PF_LEDGER_HEADERS = ['作业人员', '拍摄时间', '真实海拔', '天气', '照片']
/** 台账表 mock 数据(前 4 列文字,第 5 列「照片」渲染为缩略图占位) */
const PF_LEDGER_ROWS = [
  ['张伟', '2026-04-17 09:12', '576.4 m', '晴'],
  ['李娜', '2026-04-17 10:05', '581.2 m', '晴'],
  ['王芳', '2026-04-17 11:20', '588.7 m', '多云']
]

/** 2 个核心特性(协作共创 / 对话式优化),每个带 eyebrow + 3 条勾选 */
const PF_FEATURES: Array<{
  eyebrow: string
  accent: string
  accentBg: string
  title: string
  desc: string
  checks: string[]
}> = [
  {
    eyebrow: '不只是生成，是真填对',
    accent: '#6E3AF2',
    accentBg: '#F1ECFE',
    title: '从团队照片库，挑对的照片填进去',
    desc: 'AI 读懂每一列要什么，再按拍摄时间、地点、人物从照片库取对的那一张，每格都对位到正确的照片，填得准、对得上。',
    checks: [
      '按时间、地点、人物自动匹配',
      '整段 Word 汇报也能自动写好',
      'Excel 表格、Word 汇报、照片拼图都支持'
    ]
  },
  {
    eyebrow: '边核对边改，越改越准',
    accent: '#2E6BE6',
    accentBg: '#EAF1FE',
    title: '填得不对？在预览区说一句就改',
    desc: 'AI 不确定时会先弹窗问你确认；生成后想调整，在预览区选中单元格说一句，它只改该改的格，不打乱其它已填好的内容。',
    checks: [
      'AI 不确定时先问你确认',
      '预览区选中，说一句就重填',
      '满意了存成模板，下次一键复用'
    ]
  }
]

/** 四步流程:从上传到交付 */
const PF_STEPS: Array<{
  icon: React.ComponentType<any>
  title: string
  desc: string
}> = [
  {
    icon: UploadOutlined,
    title: '上传文档',
    desc: '拖入 Word / Excel 模板，或直接描述需求'
  },
  {
    icon: ScanOutlined,
    title: 'AI 识别表头',
    desc: '自动读懂每一列、每一段要填什么'
  },
  {
    icon: UserOutlined,
    title: '跟你确认照片/人名',
    desc: 'AI 先列好每行配哪张照片，你扫一眼确认'
  },
  {
    icon: CheckCircleOutlined,
    title: '一键生成成品',
    desc: '确认后 AI 帮你填好，导出 Word / Excel'
  }
]

/** 预览面板底部「模板使用说明」精简流程:突出照片数据自动写入文档 */
const PREVIEW_FLOW: Array<{ n: string; title: string; desc: string }> = [
  { n: '1', title: '选模板', desc: '挑好要填的表' },
  { n: '2', title: 'AI 读表头', desc: '自动识别每列填什么' },
  { n: '3', title: '照片自动入表', desc: '从水印读时间地点，照片按行归位' },
  { n: '4', title: '一键成品', desc: '导出 Word / Excel' }
]

/** 预览 / 编辑页支持的能力(对应 work 页工具栏 + 对话编辑),右栏功能介绍段 */
const PF_CAPS: Array<{
  icon: React.ComponentType<any>
  title: string
  desc: string
  color: string
  bg: string
}> = [
  {
    icon: EditOutlined,
    title: 'AI 帮你填整列 / 整行',
    desc: '选中要填的列或行，AI 一次性填好，不用一格格点',
    color: '#6E3AF2',
    bg: '#F1ECFE'
  },
  {
    icon: TableOutlined,
    title: '增删行列、改单元格',
    desc: '表格结构随手调整，不用切回 Excel / Word',
    color: '#2E6BE6',
    bg: '#EAF1FE'
  },
  {
    icon: PictureOutlined,
    title: '一张图不对，一键换',
    desc: '选中单元格，重新从团队照片库挑对的那张',
    color: '#16A06E',
    bg: '#E6F6EF'
  },
  {
    icon: DatabaseOutlined,
    title: '批量填充',
    desc: '从一批数据，一次性填好几十上百行',
    color: '#D9870B',
    bg: '#FBEFD6'
  },
  {
    icon: HighlightOutlined,
    title: '对话调美观',
    desc: '说一句，对齐、底色、字号跟着调，表格更整齐好看',
    color: '#6E3AF2',
    bg: '#F1ECFE'
  },
  {
    icon: ExportOutlined,
    title: '一键导出 / 存模板',
    desc: '成稿直接下载 Word / Excel，或存成模板下次复用',
    color: '#2E6BE6',
    bg: '#EAF1FE'
  }
]

const HomeV3: React.FC = () => {
  const navigate = useNavigate()
  // 入口来源:从 URL 的 ?from= 读取(safety=「AI 安全隐患台账」入口 / jigsaw=「照片拼图汇报」入口),
  // 据此切换左栏快捷入口展示哪一组文书。运行时校验取值,未知 / 缺失来源回落到通用组。
  const { search } = useLocation()
  const rawFrom = new URLSearchParams(search).get('from')
  const entrySource: 'safety' | 'jigsaw' | null =
    rawFrom === 'safety' || rawFrom === 'jigsaw' ? rawFrom : null
  const { setFile } = useCurrentFile()
  const { credits, refresh: refreshCredits } = useCredits()
  const payment = usePayment()
  const dispatch = useAppDispatch()
  const vipKind = useVipKind()
  // 当前团队 id:作为下方加载 effect 的依赖,团队切换时触发技能 / 最近文档重新拉取。
  // 重构版 group slice 是 RTK 普通对象,直接读 state.group.groupId(非 Immutable .get)。
  const groupId = useAppSelector((state) => state.group.groupId)
  // 6 位团队行业码(如 020010),用于按行业推荐 chip;可空(用户没设置)
  const industryCode = useAppSelector((state) => state.group.industryCode)
  const canExportShare =
    vipKind === CONFIG.VIP_KIND.VVIP ||
    vipKind === CONFIG.VIP_KIND.SVIP ||
    vipKind === CONFIG.VIP_KIND.EVIP
  const [creditsModalVisible, setCreditsModalVisible] = useState(false)
  const [pointsPackVisible, setPointsPackVisible] = useState(false)
  const [pointsCardVisible, setPointsCardVisible] = useState(false)
  const [demoVisible, setDemoVisible] = useState(false)
  const [avatarOffset, setAvatarOffset] = useState(0)
  const [genText, setGenText] = useState('')
  // 发起框待发送图片附件:回形针 / 粘贴 / 拖拽进来的图片攒在这里,输入描述后点生成
  // 一起带到 work 页(office 文件不进这里,走模板发起流程)
  const [attachments, setAttachments] = useState<ComposerAttachment[]>([])
  const [skills, setSkills] = useState<SkillItem[]>([])
  const [skillsLoading, setSkillsLoading] = useState(true)
  const [editingSkill, setEditingSkill] = useState<SkillItem | null>(null)
  const [recentFiles, setRecentFiles] = useState<FileItem[]>([])
  const [recentFilesLoading, setRecentFilesLoading] = useState(true)
  // 最近文件卡片行内改名:同一时刻只允许一个 Popover 打开,用 path 作 key。
  // busy 期间禁点其他卡片的编辑按钮避免并发写。
  const [renamingPath, setRenamingPath] = useState<string | null>(null)
  const [renameInput, setRenameInput] = useState('')
  const [renameBusy, setRenameBusy] = useState(false)

  // chip 时间选择弹窗:pendingChip=null 时不显示;选定预设后 confirm 才真发
  const [pendingChip, setPendingChip] = useState<QuickChip | null>(null)
  // hover 某个快捷入口 → 右侧浮出该模板的大预览(仅 hover 时展示)
  const [hoveredChip, setHoveredChip] = useState<QuickChip | null>(null)
  // 预览曝光埋点的停留计时器:悬停满 300ms 才算一次有效曝光,防快速划过刷量
  const hoverTrackTimerRef = useRef<number | null>(null)
  // 被余额拦截的发起动作;续费成功后自动重试
  const pendingStartRef = useRef<null | 'upload' | SkillItem>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const sampleInputRef = useRef<HTMLInputElement>(null)
  const inputRef = useRef<HTMLTextAreaElement>(null)
  const [uploading, setUploading] = useState(false)
  const [dragOverTemplate, setDragOverTemplate] = useState(false)
  const [dragOverSample, setDragOverSample] = useState(false)
  /** 用户选好的模板——延迟到点"开始识别"再上传,让用户有机会先添加样例 */
  const [templateFile, setTemplateFile] = useState<File | null>(null)
  /** 用户预先选好的"参考样例"——只存 File,不上传,diff 算完即抛 */
  const [sampleFile, setSampleFile] = useState<File | null>(null)
  const [probeResult, setProbeResult] = useState<MatchProbeResult | null>(null)
  const [probing, setProbing] = useState(false)
  const [startModalOpen, setStartModalOpen] = useState(false)
  const [giftOpen, setGiftOpen] = useState(false)
  // 团队照片总数(右栏 dropzone 副标数字),与顶栏 Header 统计同源同算法;未加载时为 null
  const photoNum = useGroupPhotoNumFormated()

  const loadSkills = useCallback(async () => {
    setSkillsLoading(true)
    try {
      const list = await xheyAi.listUserSkills()
      setSkills(
        [...list].sort((a, b) => (b.updated_at || 0) - (a.updated_at || 0))
      )
    } catch {
      setSkills([])
    } finally {
      setSkillsLoading(false)
    }
  }, [])

  const loadRecentFiles = useCallback(async () => {
    setRecentFilesLoading(true)
    try {
      const list = await xheyAi.listRecentFiles({ limit: 10 })
      setRecentFiles(
        [...list].sort((a, b) => (b.mtime_ms || 0) - (a.mtime_ms || 0))
      )
    } catch {
      setRecentFiles([])
    } finally {
      setRecentFilesLoading(false)
    }
  }, [])

  // 打开某个最近文件卡片的改名 Popover,把主名(去扩展名)预填进输入框
  const openRecentRename = useCallback(
    (f: FileItem) => {
      if (renameBusy) return
      setRenamingPath(f.path)
      const cur = f.original_name || f.filename
      const dot = cur.lastIndexOf('.')
      setRenameInput(dot > 0 ? cur.slice(0, dot) : cur)
    },
    [renameBusy]
  )

  const closeRecentRename = useCallback(() => {
    if (renameBusy) return
    setRenamingPath(null)
  }, [renameBusy])

  // 提交改名:成功后本地 patch 该行 original_name,不重拉整个列表
  const submitRecentRename = useCallback(
    async (f: FileItem) => {
      const wid = f.working_id
      const name = renameInput.trim()
      if (!wid) {
        message.warning('该文档不支持改名')
        return
      }
      if (!name) {
        message.warning('文件名不能为空')
        return
      }
      const curBase = (f.original_name || f.filename).replace(/\.[^.]+$/, '')
      if (name === curBase) {
        setRenamingPath(null)
        return
      }
      setRenameBusy(true)
      try {
        const r = await xheyAi.renameDocument(wid, name)
        setRecentFiles((prev) =>
          prev.map((x) =>
            x.working_id === wid
              ? { ...x, original_name: r.originalName || name }
              : x
          )
        )
        message.success('已重命名')
        setRenamingPath(null)
      } catch (e: any) {
        message.error(e?.message || '重命名失败')
      } finally {
        setRenameBusy(false)
      }
    },
    [renameInput]
  )

  // groupId 加入依赖:切换团队后重新加载「我的模板」与「最近填好的文档」,
  // 否则会残留上一个团队的数据(两个列表都按团队隔离,后端按 group_id 返回)。
  useEffect(() => {
    loadSkills()
    loadRecentFiles()
  }, [groupId, loadSkills, loadRecentFiles])

  // 信任栏头像轮播
  useEffect(() => {
    const t = setInterval(
      () => setAvatarOffset((o) => (o + 1) % TRUST_NAMES.length),
      2200
    )
    return () => clearInterval(t)
  }, [])

  // 页面进入埋点(只 mount 时上报一次)
  useEffect(() => {
    trackEnter('formFiller', {
      requirement_id: 1103,
      track_sign: 'aai.b8134.pageview.ai_enter_web_ai_form_filler'
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // 上传文档半屏进入埋点(Modal false → true 时上报一次)
  const startModalOpenRef = useRef(false)
  useEffect(() => {
    if (startModalOpen && !startModalOpenRef.current) {
      trackEnter('uploadDoc', {
        requirement_id: 1103,
        track_sign: 'aai.b8135.pageview.ai_enter_web_ai_upload_doc',
        enterFrom: 'web_ai_form_filler'
      })
    }
    startModalOpenRef.current = startModalOpen
  }, [startModalOpen])

  // 新用户福利弹窗展示埋点
  useEffect(() => {
    if (giftOpen) {
      trackDialogShow('formFiller', 'gift_modal', {
        requirement_id: 1103,
        track_sign: 'aai.b8134.pageview.ai_dialog_show_web_ai_form_filler'
      })
    }
  }, [giftOpen])

  // 预匹配探测:模板 + 样例都是 xlsx 时跑一次轻量字段对齐检测。debounce 300ms。
  useEffect(() => {
    if (!templateFile || !sampleFile) {
      setProbeResult(null)
      setProbing(false)
      return
    }
    const tExt = (templateFile.name.split('.').pop() || '').toLowerCase()
    const sExt = (sampleFile.name.split('.').pop() || '').toLowerCase()
    const isExcelPair =
      ['xlsx', 'xls'].includes(tExt) && ['xlsx', 'xls'].includes(sExt)
    if (!isExcelPair) {
      setProbeResult(null)
      setProbing(false)
      return
    }
    let cancelled = false
    const timer = setTimeout(async () => {
      if (cancelled) return
      setProbing(true)
      try {
        const [tBuf, sBuf] = await Promise.all([
          readFileAsArrayBuffer(templateFile),
          readFileAsArrayBuffer(sampleFile)
        ])
        if (cancelled) return
        const result = await probeTemplateSampleMatch(tBuf, sBuf)
        if (cancelled) return
        setProbeResult(result)
      } catch (e) {
        if (cancelled) return
        // eslint-disable-next-line no-console
        console.warn('[aiClerk] 预匹配探测失败:', e)
        setProbeResult({
          matchPercent: 0,
          matchedHeaders: 0,
          totalHeaders: 0,
          reversed: false,
          templateFilledCount: 0,
          sampleFilledCount: 0,
          parseError: e instanceof Error ? e.message : String(e)
        })
      } finally {
        if (!cancelled) setProbing(false)
      }
    }, 300)
    return () => {
      cancelled = true
      clearTimeout(timer)
    }
  }, [templateFile, sampleFile])

  const handleSwapTemplateAndSample = useCallback(() => {
    setTemplateFile((prev) => {
      const wasSample = sampleFile
      setSampleFile(prev)
      return wasSample
    })
  }, [sampleFile])

  const handlePickTemplate = useCallback(() => {
    if (fileInputRef.current) fileInputRef.current.click()
  }, [])

  // ─── 发起框图片附件:回形针 / 粘贴 / 拖拽进来的图片当"待发送附件"处理 ───
  // (逐字搬运 work 页 ChatPanel 的 ingestFiles,但这里只接收已确认是图片的文件;
  //  office 文件由 ingestComposerFiles 分流到模板发起流程,不进这里)
  const ingestImageFiles = useCallback((files: File[]) => {
    if (files.length === 0) return
    for (const f of files) {
      const id = `att-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`
      const ext = getFileExt(f.name)
      const kind = classifyFile(ext, f.type)
      const mediaType = resolveMediaType(f.name, f.type)
      const errMsg = validateComposerFile(f)
      if (errMsg) {
        setAttachments((prev) => [
          ...prev,
          {
            id,
            fileName: f.name,
            ext,
            mediaType,
            kind,
            size: f.size,
            status: 'error',
            error: errMsg
          }
        ])
        continue
      }
      // 图片:上传前就先 ObjectURL 显示本地缩略图,避免等远端回包
      const localPreview = kind === 'image' ? URL.createObjectURL(f) : undefined
      setAttachments((prev) => [
        ...prev,
        {
          id,
          fileName: f.name,
          ext,
          mediaType,
          kind,
          size: f.size,
          status: 'uploading',
          previewUrl: localPreview
        }
      ])
      xheyAi
        .uploadAttachment(f)
        .then((r) => {
          setAttachments((prev) =>
            prev.map((a) => {
              if (a.id !== id) return a
              // 切到远端 URL,释放本地 blob
              if (a.previewUrl) URL.revokeObjectURL(a.previewUrl)
              return {
                ...a,
                status: 'done',
                fileUrl: r.fileUrl,
                fileName: r.fileName || a.fileName,
                previewUrl: a.kind === 'image' ? r.fileUrl : undefined
              }
            })
          )
        })
        .catch((e) => {
          setAttachments((prev) =>
            prev.map((a) =>
              a.id === id
                ? {
                    ...a,
                    status: 'error',
                    error: String((e && e.message) || e || '上传失败')
                  }
                : a
            )
          )
        })
    }
  }, [])

  const removeAttachment = useCallback((id: string) => {
    setAttachments((prev) => {
      const target = prev.find((a) => a.id === id)
      if (target?.previewUrl && target.previewUrl.startsWith('blob:')) {
        URL.revokeObjectURL(target.previewUrl)
      }
      return prev.filter((a) => a.id !== id)
    })
  }, [])

  // 卸载时 revoke 残留的 ObjectURL,避免内存泄漏
  useEffect(() => {
    return () => {
      attachments.forEach((a) => {
        if (a.previewUrl && a.previewUrl.startsWith('blob:')) {
          URL.revokeObjectURL(a.previewUrl)
        }
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleClearTemplateInModal = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation()
      if (uploading) return
      trackClick('uploadDoc', 'remove_file', {
        requirement_id: 1103,
        track_sign: 'aai.b8135.click.ai_click_web_ai_upload_doc'
      })
      setTemplateFile(null)
    },
    [uploading]
  )

  // office 模板发起流程的核心(校验 + 埋点 + 打开发起弹窗)。抽出来供分流入口复用,
  // 逻辑与原 handleTemplateChange / handleTemplateDrop 的 office 分支逐字等价——
  // source 决定埋点口径:
  //   'pick' = 文件选择器(回形针 / 上传卡点击),按 startModalOpen 走 uploadDoc/formFiller
  //   'drop' = 拖拽进上传卡,固定 formFiller 且带 string1:'drag_drop'
  const startTemplateFlow = useCallback(
    (file: File, source: 'pick' | 'drop') => {
      const err = validateOfficeFile(file)
      if (err) {
        message.error(err)
        return
      }
      if (source === 'drop') {
        trackClick('formFiller', 'upload_area', {
          requirement_id: 1103,
          track_sign: 'aai.b8134.click.ai_click_web_ai_form_filler',
          string1: 'drag_drop'
        })
      } else if (startModalOpen) {
        trackClick('uploadDoc', 'upload_area', {
          requirement_id: 1103,
          track_sign: 'aai.b8135.click.ai_click_web_ai_upload_doc'
        })
      } else {
        trackClick('formFiller', 'upload_area', {
          requirement_id: 1103,
          track_sign: 'aai.b8134.click.ai_click_web_ai_form_filler'
        })
      }
      setTemplateFile(file)
      setStartModalOpen(true)
    },
    [startModalOpen]
  )

  // 分流入口:回形针 / 粘贴 / 拖拽进来的文件按类型分派——
  //   office(Word/Excel) → 走既有模板发起流程(startTemplateFlow),取第 1 个即可
  //   image → 攒成发起框待发送附件(ingestImageFiles)
  //   其它类型 → 忽略并提示(发起框只认 office / 图片)
  const ingestComposerFiles = useCallback(
    (files: File[] | FileList, source: 'pick' | 'drop') => {
      const list = Array.from(files)
      if (list.length === 0) return
      const office: File[] = []
      const images: File[] = []
      const others: File[] = []
      for (const f of list) {
        const kind = classifyFile(getFileExt(f.name), f.type)
        if (kind === 'spreadsheet' || kind === 'word') office.push(f)
        else if (kind === 'image') images.push(f)
        else others.push(f)
      }
      // office 优先:上传 Word/Excel = 用户想走模板发起,取第 1 个进流程
      if (office.length > 0) {
        startTemplateFlow(office[0], source)
      }
      if (images.length > 0) {
        ingestImageFiles(images)
      }
      // 既非 office 也非图片(PDF / PPT / 文本等)→ 发起框不支持,提示但不打断
      if (office.length === 0 && images.length === 0 && others.length > 0) {
        message.warning('发起框只支持上传 Word / Excel 或图片')
      }
    },
    [startTemplateFlow, ingestImageFiles]
  )

  const handleTemplateChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      // 先把文件复制成稳定数组,再清空 input。e.target.value='' 会把 e.target.files
      // (对输入框的实时引用)一并清空;若清空后再读 files.length 得到的是 0 —— 这正是
      // "点回形针选完文件没反应"的根因(拖拽走 e.dataTransfer 不受影响,所以拖拽一直正常)。
      const files = e.target.files ? Array.from(e.target.files) : []
      e.target.value = '' // 放在复制之后:允许重复选同一个文件
      if (files.length > 0) ingestComposerFiles(files, 'pick')
    },
    [ingestComposerFiles]
  )

  const handleStartModalClose = useCallback(() => {
    if (uploading) return
    trackClick('uploadDoc', 'close', {
      requirement_id: 1103,
      track_sign: 'aai.b8135.click.ai_click_web_ai_upload_doc'
    })
    setStartModalOpen(false)
    setTemplateFile(null)
    setSampleFile(null)
  }, [uploading])

  const handlePickSample = useCallback((e: React.MouseEvent) => {
    e.stopPropagation()
    trackClick('uploadDoc', 'upload_sample', {
      requirement_id: 1103,
      track_sign: 'aai.b8135.click.ai_click_web_ai_upload_doc'
    })
    if (sampleInputRef.current) sampleInputRef.current.click()
  }, [])

  const handleSampleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0]
      e.target.value = ''
      if (!file) return
      const err = validateOfficeFile(file)
      if (err) {
        message.error(`参考样例：${err}`)
        return
      }
      setSampleFile(file)
    },
    []
  )

  const handleSampleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setDragOverSample(true)
  }, [])
  const handleSampleDragLeave = useCallback(() => {
    setDragOverSample(false)
  }, [])
  const handleSampleDrop = useCallback(async (e: React.DragEvent) => {
    e.preventDefault()
    setDragOverSample(false)
    const file = e.dataTransfer.files?.[0]
    if (!file) return
    const err = validateOfficeFile(file)
    if (err) {
      message.error(`参考样例：${err}`)
      return
    }
    setSampleFile(file)
  }, [])

  const handleTemplateDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setDragOverTemplate(true)
  }, [])
  const handleTemplateDragLeave = useCallback(() => {
    setDragOverTemplate(false)
  }, [])
  const handleTemplateDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault()
      setDragOverTemplate(false)
      if (e.dataTransfer?.files?.length) {
        ingestComposerFiles(e.dataTransfer.files, 'drop')
      }
    },
    [ingestComposerFiles]
  )

  // 发起框(描述框)拖拽:office 拖这儿分流到模板发起、图片拖这儿变待发送附件。
  // 关键:onDragOver 必须 preventDefault,否则 drop 事件根本不触发(浏览器走默认=打开文件)。
  // 不做拖拽高亮:发起框区域被 textarea 盖住,高亮会被输入框遮挡、观感差。
  const handleComposerDragOver = useCallback((e: React.DragEvent) => {
    if (e.dataTransfer?.types?.includes('Files')) e.preventDefault()
  }, [])
  const handleComposerDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault()
      if (e.dataTransfer?.files?.length) {
        ingestComposerFiles(e.dataTransfer.files, 'drop')
      }
    },
    [ingestComposerFiles]
  )

  // 发起框粘贴:抽出剪贴板里的图片 / office 文件交给分流入口(纯文本不拦,正常粘贴)。
  // (逐字搬运 work 页 ChatPanel 的 handlePaste,分流由 ingestComposerFiles 承担)
  const handlePaste = useCallback(
    (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
      const cd = e.clipboardData
      if (!cd) return
      // ① 优先 clipboardData.files —— 从文件系统复制的文件;Chrome 粘贴截图也常落这里
      const files: File[] =
        cd.files && cd.files.length > 0 ? Array.from(cd.files) : []
      // ② 兜底 clipboardData.items —— 从网页 / App 复制的图片、部分浏览器截图只在
      //    items 里(此时 files 为空)。抽出 image/* 的项,给没文件名的粘贴图补个名,
      //    否则附件卡片名会是空 / "image.png"。
      if (files.length === 0 && cd.items && cd.items.length > 0) {
        for (const it of Array.from(cd.items)) {
          if (it.kind !== 'file' || !it.type.startsWith('image/')) continue
          const raw = it.getAsFile()
          if (!raw) continue
          if (raw.name && /\.[a-z0-9]+$/i.test(raw.name)) {
            files.push(raw)
          } else {
            const ext = (it.type.split('/')[1] || 'png').split('+')[0]
            files.push(
              new File([raw], `粘贴图片-${Date.now()}.${ext}`, {
                type: it.type
              })
            )
          }
        }
      }
      if (files.length > 0) {
        // 有图片 / 文件 → 拦默认粘贴(否则文件名 / 图片二进制会以文本落进输入框),
        // 交给分流入口(图片进附件、office 进模板流程)
        e.preventDefault()
        ingestComposerFiles(files, 'pick')
        return
      }
      // ③ 没拿到内容、但明显是"复制了文件"(从 Finder / 资源管理器复制文件时,浏览器
      //    paste 读不到文件字节、只给文件名)→ 别把文件名当文本贴进去,拦住并提示改用
      //    拖拽 / 回形针。判据:types 含 'Files',或整段是个带图片/文档扩展名的裸文件名。
      const plainText = (cd.getData && cd.getData('text/plain')) || ''
      const looksLikeFileCopy =
        (cd.types && Array.from(cd.types).includes('Files')) ||
        /^[^\s/\\]+\.(png|jpe?g|gif|webp|bmp|heic|heif|svg|pdf|docx?|xlsx?|pptx?|csv)$/i.test(
          plainText.trim()
        )
      if (looksLikeFileCopy) {
        e.preventDefault()
        message.info(
          '复制的图片文件无法直接粘贴内容，请把文件拖进输入框，或点回形针选择'
        )
      }
      // 其余(真·文本)→ 不拦,正常粘贴
    },
    [ingestComposerFiles]
  )

  /**
   * 用户点击发起会话前:确保 credits 已就绪(瞬态故障时主动补 refresh 一次)。
   *   - { ok:true, credits }:有效余额
   *   - { ok:false }:已 message.error「网络错误，请刷新重试」,调用方 return
   */
  const ensureCreditsReady = useCallback(async (): Promise<{
    ok: boolean
    credits: CreditsBalance | null
  }> => {
    let c: CreditsBalance | null = credits
    if (!c || c.available == null) {
      c = await refreshCredits()
    }
    if (!c || c.available == null) {
      message.error('网络错误，请刷新重试')
      return { ok: false, credits: null }
    }
    return { ok: true, credits: c }
  }, [credits, refreshCredits])

  // 真正的上传 + diff + 跳转——点"开始识别"才触发
  const handleStart = useCallback(
    async (skipGuard = false) => {
      if (uploading) return
      if (!skipGuard) {
        const ready = await ensureCreditsReady()
        if (!ready.ok) return
        if ((ready.credits?.available ?? 0) <= 0) {
          pendingStartRef.current = 'upload'
          setCreditsModalVisible(true)
          return
        }
      }
      if (!templateFile) {
        message.error('请先选择要填写的模板')
        return
      }
      trackClick('uploadDoc', 'start_recognition', {
        requirement_id: 1103,
        track_sign: 'aai.b8135.click.ai_click_web_ai_upload_doc'
      })
      const tExt = (templateFile.name.split('.').pop() || '').toLowerCase()
      const sExt = sampleFile
        ? (sampleFile.name.split('.').pop() || '').toLowerCase()
        : ''
      const isExcelPair =
        ['xlsx', 'xls'].includes(tExt) && ['xlsx', 'xls'].includes(sExt)
      const isWordPair =
        ['docx', 'doc'].includes(tExt) && ['docx', 'doc'].includes(sExt)
      const sampleUsable = !!sampleFile && (isExcelPair || isWordPair)
      if (sampleFile && !sampleUsable) {
        message.warning(
          '参考样例必须与模板同类型（Excel↔Excel / Word↔Word），本次忽略样例继续上传'
        )
      }
      setUploading(true)
      try {
        const safeTemplate = ensureSafeFile(templateFile)
        const safeSample =
          sampleUsable && sampleFile ? ensureSafeFile(sampleFile) : null
        const t0 = Date.now()
        let templateCostTime = 0
        let sampleCostTime = 0
        const [uploadResult, sampleUploadResult, templateBuf, sampleBuf] =
          await Promise.all([
            xheyAi.upload(safeTemplate).then((r) => {
              templateCostTime = Date.now() - t0
              return r
            }),
            safeSample
              ? xheyAi.uploadAttachment(safeSample).then((r) => {
                  sampleCostTime = Date.now() - t0
                  return r
                })
              : Promise.resolve(null),
            isExcelPair
              ? readFileAsArrayBuffer(templateFile)
              : Promise.resolve(null),
            isExcelPair && sampleFile
              ? readFileAsArrayBuffer(sampleFile)
              : Promise.resolve(null)
          ])
        const tFmt = tExt === 'docx' || tExt === 'xlsx' ? tExt : 'other'
        trackResult('uploadDoc', 'upload_doc', {
          requirement_id: 1103,
          track_sign: 'aai.b8135.click.ai_result_web_ai_upload_doc',
          actionResult: 'success',
          string1: tFmt,
          number1: Math.round(safeTemplate.size / 1024),
          bool1: !!sampleUploadResult,
          costTime: templateCostTime
        })
        if (safeSample && sampleUploadResult) {
          const sFmt = sExt === 'docx' || sExt === 'xlsx' ? sExt : 'other'
          trackResult('uploadDoc', 'upload_sample', {
            requirement_id: 1103,
            track_sign: 'aai.b8135.click.ai_result_web_ai_upload_doc',
            actionResult: 'success',
            string1: sFmt,
            number1: Math.round(safeSample.size / 1024),
            costTime: sampleCostTime
          })
        }
        const { fileUrl, fileName, filePath, refreshUrl, workingId } =
          uploadResult
        if (sampleUsable && safeSample && sampleUploadResult) {
          let diffText = ''
          if (isExcelPair && templateBuf && sampleBuf) {
            try {
              const diff = await computeExcelDiff(
                templateBuf,
                sampleBuf,
                safeTemplate.name,
                safeSample.name
              )
              diffText = diffToPromptText(diff)
            } catch (e) {
              // eslint-disable-next-line no-console
              console.warn('[aiClerk] 样例 diff 计算失败:', e)
              const detail = e instanceof Error ? e.message : String(e)
              message.warning({
                content: `参考样例解析失败：${detail}（继续按附件方式发给 agent）`,
                duration: 6
              })
            }
          }
          const payload: SamplePayload = {
            diffText,
            attachment: {
              url: sampleUploadResult.fileUrl,
              name: safeSample.name,
              mediaType: pickMediaType(safeSample.name)
            }
          }
          window.sessionStorage.setItem(
            SAMPLE_DIFF_PROMPT_KEY,
            JSON.stringify(payload)
          )
        } else {
          window.sessionStorage.removeItem(SAMPLE_DIFF_PROMPT_KEY)
        }
        setFile({ fileUrl, fileName, filePath, refreshUrl, workingId })
        try {
          window.localStorage.removeItem('aiClerk:conversationId')
        } catch {
          /* 忽略 */
        }
        navigate('/ai/clerk/work')
      } catch (e) {
        const errMsg = e instanceof Error ? e.message : String(e)
        message.error(`上传失败：${errMsg}`)
        const tFmtRaw = (templateFile.name.split('.').pop() || '').toLowerCase()
        const tFmt =
          tFmtRaw === 'docx' || tFmtRaw === 'xlsx' ? tFmtRaw : 'other'
        trackResult('uploadDoc', 'upload_doc', {
          requirement_id: 1103,
          track_sign: 'aai.b8135.click.ai_result_web_ai_upload_doc',
          actionResult: 'fail',
          string1: tFmt,
          number1: Math.round(templateFile.size / 1024),
          bool1: !!sampleFile,
          errorMsg: errMsg
        })
        setUploading(false)
      }
    },
    [navigate, uploading, setFile, templateFile, sampleFile, ensureCreditsReady]
  )

  const startTemplate = useCallback(
    async (sk: SkillItem) => {
      trackClick('formFiller', 'continue_use_template', {
        requirement_id: 1103,
        track_sign: 'aai.b8134.click.ai_click_web_ai_form_filler',
        string1: sk.name
      })
      try {
        const result = await xheyAi.useSkill(sk.name)
        try {
          window.localStorage.removeItem('aiClerk:conversationId')
        } catch {
          /* 忽略 */
        }
        if (result.hasTemplate) {
          setFile({
            fileUrl: result.fileUrl,
            fileName: result.fileName,
            filePath: result.filePath,
            refreshUrl: result.refreshUrl
          })
        }
        const qs =
          `?skill=${encodeURIComponent(sk.name)}` +
          `&skillTitle=${encodeURIComponent(sk.display_name || sk.name)}`
        navigate(`/ai/clerk/work${qs}`)
      } catch (e) {
        message.error(
          `继续使用失败: ${e instanceof Error ? e.message : String(e)}`
        )
      }
    },
    [navigate, setFile]
  )

  // 续费成功后放行容错:续上被拦截的上传或模板发起
  const runPendingStart = useCallback(() => {
    const pending = pendingStartRef.current
    if (!pending) return
    pendingStartRef.current = null
    if (pending === 'upload') {
      handleStart(true)
    } else {
      startTemplate(pending)
    }
  }, [handleStart, startTemplate])

  // 会员升级成功(vipKind 变化)+ 有待发起动作 → 刷新余额 + 放行容错续会话
  const prevVipKindRef = useRef(vipKind)
  useEffect(() => {
    if (prevVipKindRef.current !== vipKind) {
      refreshCredits()
      if (pendingStartRef.current) {
        runPendingStart()
      }
    }
    prevVipKindRef.current = vipKind
  }, [vipKind, refreshCredits, runPendingStart])

  const handleGenerate = useCallback(
    async (
      overrideText?: string,
      templateHint?: { fileName: string; fileType: 'excel' | 'word' },
      // 英文 taskName 单独传(不进 upload):只用于路由 scene + 首条会话隐藏发给 LLM。
      taskName?: string,
      // 快捷入口发起时带上埋点信息:入口名 + 时间口径,写进 generate_form,
      // 好把「点击 quick_chip → 真正发送(带成败)」这条漏斗打通,并统计时间口径偏好。
      // 一句话描述生成入口不传 → generate_form 不带这两个字段。
      trackMeta?: { chipLabel?: string; rangeLabel?: string }
    ) => {
      if (uploading) return
      const text = (
        typeof overrideText === 'string' ? overrideText : genText
      ).trim()
      if (!text) return
      // 有图片附件还在上传时先等一等,别丢了正上传的图(附件是辅助,描述仍是必填主体)
      if (attachments.some((a) => a.status === 'uploading')) {
        message.warning('图片还在上传，请稍候')
        return
      }
      // 快捷入口来源标记:有 chipLabel 才带 string1/string2,一句话入口为空
      const chipTrackExtra = trackMeta?.chipLabel
        ? { string1: trackMeta.chipLabel, string2: trackMeta.rangeLabel || '' }
        : {}
      // 「用一句话生成空白表」入口:点击 → 余额校验 → upload(空文档,只带描述) → 跳 work
      trackClick('formFiller', 'generate_form', {
        requirement_id: 1103,
        track_sign: 'aai.b8134.click.ai_click_web_ai_form_filler',
        number1: text.length,
        ...chipTrackExtra
      })
      const ready = await ensureCreditsReady()
      if (!ready.ok) return
      if ((ready.credits?.available ?? 0) <= 0) {
        pendingStartRef.current = 'upload'
        setCreditsModalVisible(true)
        return
      }
      setUploading(true)
      const t0 = Date.now()
      try {
        const { fileUrl, fileName, filePath, refreshUrl, workingId } =
          await xheyAi.upload(undefined, templateHint)
        setFile({ fileUrl, fileName, filePath, refreshUrl, workingId })
        try {
          window.sessionStorage.setItem(DESCRIBE_PROMPT_KEY, text)
        } catch {
          /* 忽略 */
        }
        // taskName 存在 ⟺ 该入口有预生成模板(taskName 只从模板库派生)。
        //   · 有模板:后端已按 fileName 匹配到现成模板,work 页首条走 upload_doc 场景
        //     (「填这份文档」),并把 taskName 存下、首条会话隐藏注入 <aiclerk-task-name>
        //     帮 LLM 锚定任务类型(upload 不带 taskName,只在这条隐藏消息里发)。
        //   · 无模板(部分兜底/安全/拼图入口)或一句话描述生成:清掉 entryScene/taskName,
        //     落回 blank_describe(后端「先建空白表结构」),避免空白文档走 upload_doc 退化。
        try {
          if (taskName) {
            window.sessionStorage.setItem('aiClerk:entryScene', 'upload_doc')
            window.sessionStorage.setItem('aiClerk:taskName', taskName)
          } else {
            window.sessionStorage.removeItem('aiClerk:entryScene')
            window.sessionStorage.removeItem('aiClerk:taskName')
          }
        } catch {
          /* 忽略 */
        }
        // 待发送图片附件(仅上传完成的)随描述一起带到 work 页首条消息;没有则清掉该
        // key,避免上次残留被 work 页脏读
        try {
          const doneAttachments = attachments
            .filter((a) => a.status === 'done' && a.fileUrl)
            .map((a) => ({
              url: a.fileUrl as string,
              name: a.fileName,
              mediaType: a.mediaType
            }))
          if (doneAttachments.length > 0) {
            window.sessionStorage.setItem(
              DESCRIBE_ATTACHMENTS_KEY,
              JSON.stringify(doneAttachments)
            )
          } else {
            window.sessionStorage.removeItem(DESCRIBE_ATTACHMENTS_KEY)
          }
        } catch {
          /* 忽略 */
        }
        try {
          window.localStorage.removeItem('aiClerk:conversationId')
        } catch {
          /* 忽略 */
        }
        trackResult('formFiller', 'generate_form', {
          requirement_id: 1103,
          track_sign: 'aai.b8134.click.ai_result_web_ai_form_filler',
          actionResult: 'success',
          number1: text.length,
          costTime: Date.now() - t0,
          ...chipTrackExtra
        })
        navigate('/ai/clerk/work')
      } catch (e) {
        const errMsg = e instanceof Error ? e.message : String(e)
        message.error(`生成失败：${errMsg}`)
        trackResult('formFiller', 'generate_form', {
          requirement_id: 1103,
          track_sign: 'aai.b8134.click.ai_result_web_ai_form_filler',
          actionResult: 'fail',
          number1: text.length,
          costTime: Date.now() - t0,
          errorMsg: errMsg,
          ...chipTrackExtra
        })
        setUploading(false)
      }
    },
    [uploading, genText, attachments, ensureCreditsReady, setFile, navigate]
  )

  const handleWatchDemo = useCallback(() => {
    trackClick('formFiller', 'view_demo', {
      requirement_id: 1103,
      track_sign: 'aai.b8134.click.ai_click_web_ai_form_filler'
    })
    setDemoVisible(true)
  }, [])

  const handleDescribeCta = useCallback(() => {
    trackClick('formFiller', 'describe_cta', {
      requirement_id: 1103,
      track_sign: 'aai.b8134.click.ai_click_web_ai_form_filler'
    })
    const el = inputRef.current
    if (!el) return
    el.focus()
    el.classList.add('is-highlight')
    const onEnd = () => {
      el.classList.remove('is-highlight')
      el.removeEventListener('animationend', onEnd)
    }
    el.addEventListener('animationend', onEnd)
  }, [])

  // 积分入口 CTA:增购积分包(超级会员)/ 升级会员(其它),参照 work handlePointsCta
  const handlePointsCta = useCallback(
    (ctx: { action: 'upgrade' | 'addon' }) => {
      setPointsCardVisible(false)
      if (ctx.action === 'addon') {
        setPointsPackVisible(true)
      } else {
        payment.openVipByKind(
          vipKind || CONFIG.VIP_KIND.VIP,
          'aiFormFiller',
          false,
          aiClerkPayContent({
            isVip: vipKind > 0,
            vipKind,
            usedPoints: credits?.consumed ?? 0
          })
        )
      }
    },
    [payment, vipKind, credits]
  )

  // chip 点击 = 先弹时间选择弹窗,不再直接发送。埋点先打(用户已表达意图),
  // 时间预设每次弹出都回到"本月"默认(不跨 chip 记住),避免用户在两个 chip 之间来回时
  // 上一次的时间残留误发。
  const handleChipClick = useCallback(
    (chip: QuickChip) => {
      trackClick('formFiller', 'quick_chip', {
        requirement_id: 1103,
        track_sign: 'aai.b8134.click.ai_click_web_ai_form_filler',
        string1: chip.label,
        string2: entrySource || 'default'
      })
      // v4 能力增强 chip(条码/图像识别/隐患识别/质检)需 SVIP 及以上。
      // vipKind 权益从低到高:VIP(1) < VVIP(3) < SVIP(2) < EVIP(4);数值不比大小,
      // 只有 SVIP 或 EVIP 才算达标。未达 → 弹 SVIP 升级弹窗,不进时间选择。
      if (
        chip.svipOnly &&
        vipKind !== CONFIG.VIP_KIND.SVIP &&
        vipKind !== CONFIG.VIP_KIND.EVIP
      ) {
        trackClick('formFiller', 'quick_chip_svip_block', {
          requirement_id: 1103,
          track_sign: 'aai.b8134.click.ai_click_web_ai_form_filler',
          string1: chip.label
        })
        // 这些 chip 用了 AI 能力,是「只能开 SVIP」的卡点 → onlySvip=true 锁死只显示 SVIP
        // tab(不让用户切到 VIP/VVIP,那些买了也解不了锁)。openVipByKind 把 onlySvip 写死
        // 成 false 又是禁改的打开链路,openSvip 不接 customContent → 直接 dispatch 现有
        // setPaymentVisible(既锁 tab 又带 aiClerk 定制文案),不改任何共享/保护文件。
        dispatch(
          setPaymentVisible(
            true,
            'aiFormFiller',
            CONFIG.VIP_KIND.SVIP,
            false,
            true, // onlySvip
            false, // onlyVVip
            aiClerkPayContent({
              isVip: vipKind > 0,
              vipKind,
              usedPoints: credits?.consumed ?? 0
            })
          )
        )
        return
      }
      // ChipTimePicker 每次打开自动重置到"本月",不用在这里做默认值
      setPendingChip(chip)
    },
    [entrySource, vipKind, dispatch, credits]
  )

  const closeChipTimePicker = useCallback(() => {
    if (uploading) return
    // 埋点:点了快捷入口、弹出时间选择弹窗后又取消/关闭(没发送),统计放弃率
    if (pendingChip) {
      trackClick('formFiller', 'quick_chip_time_cancel', {
        requirement_id: 1103,
        track_sign: 'aai.b8134.click.ai_click_web_ai_form_filler',
        string1: pendingChip.label
      })
    }
    setPendingChip(null)
  }, [uploading, pendingChip])

  // ChipTimePicker onConfirm 回来的 TimeRange → 拼消息 + 走 handleGenerate。
  // message 末尾追加"数据范围: YYYY-MM-DD 至 YYYY-MM-DD(<preset label>)",
  // 让 AI 一眼看出用户挑的是"本月"这种口径,回复时可以用同款措辞。
  const confirmChipTime = useCallback(
    (range: TimeRange) => {
      if (!pendingChip) return
      const chip = pendingChip
      const dateStr = `${range.start.format('YYYY-MM-DD')} 至 ${range.end.format('YYYY-MM-DD')}`
      const finalMessage = `${chip.message}\n数据范围: ${dateStr}(${range.label})`
      // 输入框回显短标题 + 时间,让用户从视觉上看到"发出去的带时间"
      setGenText(`${chip.label} · ${range.label}`)
      setPendingChip(null)
      // 入口名(chip.label)+ 英文 taskName + 类型(excel/word)当模板匹配键透传给 upload,
      // 后端据此从 OSS 预生成模板库拉对应模板;匹配不到再回退空白。taskName 同时会被
      // 存下,首条会话里隐藏发给 LLM 帮助理解任务类型。
      handleGenerate(
        finalMessage,
        { fileName: chip.label, fileType: chipDocType(chip.label) },
        getChipTaskName(chip.label),
        // 埋点:generate_form 带上入口名 + 时间口径,打通「点 chip → 发送成败」漏斗
        { chipLabel: chip.label, rangeLabel: range.label }
      )
    },
    [pendingChip, handleGenerate]
  )

  const handleQuickFill = useCallback((name: string) => {
    trackClick('formFiller', 'supported_type_chip', {
      requirement_id: 1103,
      track_sign: 'aai.b8134.click.ai_click_web_ai_form_filler',
      string1: name
    })
    setGenText(`做一份本月${name}，使用我的团队已有照片`)
    const el = inputRef.current
    if (!el) return
    el.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
    el.focus()
    el.classList.remove('is-highlight')
    void el.offsetWidth
    el.classList.add('is-highlight')
    const onEnd = () => {
      el.classList.remove('is-highlight')
      el.removeEventListener('animationend', onEnd)
    }
    el.addEventListener('animationend', onEnd)
  }, [])

  // 两个接口都完成后才判断「我的模板 / 最近文档」要不要展示,加载期间不渲染避免闪烁
  const isLoading = skillsLoading || recentFilesLoading
  const hasHistory = !isLoading && (skills.length > 0 || recentFiles.length > 0)

  const photoNumText = photoNum
    ? photoNum.unit === '万'
      ? `${photoNum.value}万`
      : `${photoNum.value}`
    : '28 万'

  return (
    <div className="aiClerk-homeV3">
      <input
        ref={fileInputRef}
        type="file"
        accept={COMPOSER_ACCEPT_TYPES}
        hidden
        onChange={handleTemplateChange}
      />

      {/* ── 主体:左 AI 对话面板(含上传入口) + 右 引导/模板/最近 ──
          顶栏删除:全站已有顶栏(团队/会员/头像),V3 不再重复一层 */}
      <div className="aiClerk-homeV3__body">
        {/* 左:上传入口(缩小,置顶) + AI 引导对话 */}
        <aside className="aiClerk-homeV3__chat">
          {/* 标题对齐 work 页 ChatPanel header:✦ 紫渐变圆 + 名称 */}
          <div className="aiClerk-homeV3__chat-head">
            <span className="aiClerk-homeV3__chat-avatar">✦</span>
            <div className="aiClerk-homeV3__chat-headtext">
              <div className="aiClerk-homeV3__chat-name">AI 填表员</div>
              <div className="aiClerk-homeV3__chat-status">
                在线 · 帮你把照片填进表格
              </div>
            </div>
          </div>

          <div className="aiClerk-homeV3__chat-body">
            {/* 上传区:头部说明(价值主张) + 上传卡 */}
            <div className="aiClerk-homeV3__sec-hint">
              上传模板，我从团队照片库按水印内容、时间挑好对应照片，整份自动填好——省下大半天手工。
            </div>
            <div
              className={`aiClerk-homeV3__upload-card${
                dragOverTemplate ? ' is-drag-over' : ''
              }`}
              onClick={handlePickTemplate}
              onDragOver={handleTemplateDragOver}
              onDragLeave={handleTemplateDragLeave}
              onDrop={handleTemplateDrop}
            >
              <span className="aiClerk-homeV3__upload-card-icon">
                <CloudUploadOutlined />
              </span>
              <div className="aiClerk-homeV3__upload-card-text">
                <div className="aiClerk-homeV3__upload-card-title">
                  上传要填的 Word / Excel
                </div>
                <div className="aiClerk-homeV3__upload-card-sub">
                  拖进来或点击 · 照片 AI 来配
                </div>
              </div>
              <button
                type="button"
                className="aiClerk-homeV3__upload-card-btn"
                onClick={(e) => {
                  e.stopPropagation()
                  handlePickTemplate()
                }}
                disabled={uploading}
              >
                上传
              </button>
            </div>

            {/* 快捷生成区:头部说明 + 快捷 chip */}
            <div className="aiClerk-homeV3__sec-hint">
              {entrySource === 'safety'
                ? '针对安全隐患台账，可以直接生成这些文书：'
                : entrySource === 'jigsaw'
                  ? '我还可以生成拼图汇报文档：'
                  : '没有文档模板也能填写：在下面说一句要什么表，我先做一份，再帮你填好。'}
            </div>

            <div className="aiClerk-homeV3__chat-chips">
              {resolveQuickChips(entrySource, industryCode).map((c) => (
                <button
                  key={c.label}
                  type="button"
                  className={`aiClerk-homeV3__chip${
                    c.svipOnly ? ' aiClerk-homeV3__chip--svip' : ''
                  }`}
                  onClick={() => handleChipClick(c)}
                  onMouseEnter={() => {
                    setHoveredChip(c)
                    // 悬停满 300ms 才算一次有效预览曝光,埋点评估预览用不用得上
                    if (hoverTrackTimerRef.current) {
                      window.clearTimeout(hoverTrackTimerRef.current)
                    }
                    hoverTrackTimerRef.current = window.setTimeout(() => {
                      trackClick('formFiller', 'quick_chip_preview_show', {
                        requirement_id: 1103,
                        track_sign:
                          'aai.b8134.click.ai_click_web_ai_form_filler',
                        string1: c.label,
                        string2: entrySource || 'default'
                      })
                    }, 300)
                  }}
                  onMouseLeave={() => {
                    if (hoverTrackTimerRef.current) {
                      window.clearTimeout(hoverTrackTimerRef.current)
                      hoverTrackTimerRef.current = null
                    }
                    setHoveredChip((prev) => (prev === c ? null : prev))
                  }}
                  disabled={uploading}
                >
                  {/* v4 SVIP 专属标签:悬浮在 chip 右上角,金渐变胶囊,不与 chip 主体挤压布局 */}
                  {c.svipOnly && (
                    <span
                      className="aiClerk-homeV3__chip-svip-badge"
                      aria-label="SVIP 专属"
                    >
                      SVIP
                    </span>
                  )}
                  <ChipIcon
                    name={c.icon}
                    className="aiClerk-homeV3__chip-icon"
                  />
                  <span className="aiClerk-homeV3__chip-text">{c.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* 输入框样式对齐 work 页 ChatPanel:灰底圆角 textarea + 左下纸夹 + 右下发送 */}
          <div
            className="aiClerk-homeV3__chat-input"
            onDragOver={handleComposerDragOver}
            onDrop={handleComposerDrop}
          >
            {/* 待发送图片附件:攒在输入框上方,可删缩略图卡片(uploading/error 态) */}
            {attachments.length > 0 && (
              <div className="aiClerk-homeV3__atts">
                {attachments.map((a) => (
                  <div
                    key={a.id}
                    className={`aiClerk-homeV3__att${
                      a.status === 'error' ? ' is-error' : ''
                    }`}
                    title={
                      a.status === 'error' ? a.error || '上传失败' : a.fileName
                    }
                  >
                    <div className="aiClerk-homeV3__att-thumb">
                      {a.previewUrl ? (
                        <img src={a.previewUrl} alt={a.fileName} />
                      ) : (
                        <PictureOutlined />
                      )}
                      {a.status === 'uploading' && (
                        <span className="aiClerk-homeV3__att-mask">
                          <LoadingOutlined />
                        </span>
                      )}
                    </div>
                    <span className="aiClerk-homeV3__att-name">
                      {a.fileName}
                    </span>
                    <button
                      type="button"
                      className="aiClerk-homeV3__att-del"
                      onClick={() => removeAttachment(a.id)}
                      aria-label="删除附件"
                    >
                      <CloseOutlined />
                    </button>
                  </div>
                ))}
              </div>
            )}
            <div className="aiClerk-homeV3__input-wrap">
              <textarea
                ref={inputRef}
                className="aiClerk-homeV3__input"
                placeholder={`例如：把 ${
                  new Date().getMonth() + 1
                } 月的现场照片整理成验收 / 巡检记录表，要照片、时间、地点……`}
                value={genText}
                onChange={(e) => setGenText(e.target.value)}
                onPaste={handlePaste}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault()
                    handleGenerate()
                  }
                }}
                disabled={uploading}
              />
              <button
                type="button"
                className="aiClerk-homeV3__attach-btn"
                onClick={handlePickTemplate}
                disabled={uploading}
                aria-label="上传文档"
              >
                <PaperClipOutlined />
              </button>
              <button
                type="button"
                className="aiClerk-homeV3__send-btn"
                onClick={() => handleGenerate()}
                disabled={uploading || !genText.trim()}
                aria-label="生成"
              >
                <ArrowUpOutlined />
              </button>
            </div>
            {/* 输入框下积分余额(参照 work composerFooter):chip + hover 弹 PointsStatusCard */}
            {credits &&
              credits.total_quota != null &&
              credits.consumed != null && (
                <div className="aiClerk-homeV3__points">
                  <div
                    className="aiClerk-homeV3__points-hover"
                    onMouseEnter={() => setPointsCardVisible(true)}
                    onMouseLeave={() => setPointsCardVisible(false)}
                  >
                    {pointsCardVisible && (
                      <div className="aiClerk-homeV3__points-popover">
                        <PointsStatusCard
                          vipKind={vipKind}
                          total={credits.total_quota}
                          used={credits.consumed}
                          perQueryCost={SINGLE_QUERY_COST}
                          onCtaClick={handlePointsCta}
                        />
                      </div>
                    )}
                    <PointsEntryChip
                      vipKind={vipKind}
                      total={credits.total_quota}
                      used={credits.consumed}
                      perQueryCost={SINGLE_QUERY_COST}
                      onCtaClick={handlePointsCta}
                    />
                  </div>
                </div>
              )}
          </div>
        </aside>

        {/* 右:新用户引导 / 老用户模板+最近(上传入口已移到左栏顶部) */}
        <div className="aiClerk-homeV3__main">
          {/* 礼盒入口:非会员 + 积分未消耗过 + 无历史(既没建过模板也没生成过文档)时展示。
              「有最近填好的文档 = 已经用过 = 不该再当新手引导」——避免用户明明用过还看到礼盒。 */}
          {!vipKind && (credits?.consumed ?? 1) <= 0 && !hasHistory && (
            <button
              type="button"
              className="aiClerk-homeV3__gift-entry"
              onClick={() => {
                trackClick('formFiller', 'gift_entry', {
                  requirement_id: 1103,
                  track_sign: 'aai.b8134.click.ai_click_web_ai_form_filler'
                })
                setGiftOpen(true)
              }}
              aria-label="新用户福利"
            >
              <GiftFilled />
              <span className="aiClerk-homeV3__gift-entry-dot" />
            </button>
          )}
          {hasHistory ? (
            <div className="aiClerk-homeV3__history">
              {skills.length > 0 && (
                <section className="aiClerk-homeV3__panel">
                  <div className="aiClerk-homeV3__panel-head">
                    <span className="aiClerk-homeV3__panel-bar" />
                    <h2 className="aiClerk-homeV3__panel-title">我的模板</h2>
                  </div>
                  <div className="aiClerk-homeV3__tpl-list">
                    {skills.map((sk) => (
                      <div key={sk.name} className="aiClerk-homeV3__tpl">
                        <span className="aiClerk-homeV3__tpl-icon">
                          <FileTextFilled />
                        </span>
                        <div className="aiClerk-homeV3__tpl-main">
                          <div className="aiClerk-homeV3__tpl-name">
                            {sk.display_name}
                          </div>
                          {sk.updated_at && (
                            <div className="aiClerk-homeV3__tpl-meta">
                              {formatSessionTime(sk.updated_at)}
                            </div>
                          )}
                        </div>
                        <div className="aiClerk-homeV3__tpl-actions">
                          <button
                            type="button"
                            className="aiClerk-homeV3__tpl-use"
                            onClick={async () => {
                              const ready = await ensureCreditsReady()
                              if (!ready.ok) return
                              if ((ready.credits?.available ?? 0) <= 0) {
                                pendingStartRef.current = sk
                                setCreditsModalVisible(true)
                                return
                              }
                              startTemplate(sk)
                            }}
                          >
                            继续用
                          </button>
                          <button
                            type="button"
                            className="aiClerk-homeV3__tpl-icbtn"
                            aria-label="编辑"
                            onClick={() => {
                              trackClick('formFiller', 'edit_template', {
                                requirement_id: 1103,
                                track_sign:
                                  'aai.b8134.click.ai_click_web_ai_form_filler',
                                string1: sk.name
                              })
                              setEditingSkill(sk)
                            }}
                          >
                            <EditOutlined />
                          </button>
                          <button
                            type="button"
                            className="aiClerk-homeV3__tpl-icbtn aiClerk-homeV3__tpl-icbtn--danger"
                            aria-label="删除"
                            onClick={() => {
                              trackClick('formFiller', 'delete_template', {
                                requirement_id: 1103,
                                track_sign:
                                  'aai.b8134.click.ai_click_web_ai_form_filler',
                                string1: sk.name
                              })
                              trackDialogShow(
                                'formFiller',
                                'confirm_delete_template',
                                {
                                  requirement_id: 1103,
                                  track_sign:
                                    'aai.b8134.pageview.ai_dialog_show_web_ai_form_filler'
                                }
                              )
                              Modal.confirm({
                                title: `删除模板「${
                                  sk.display_name || sk.name
                                }」?`,
                                content:
                                  '删除后无法恢复，模板里的示例文件 / 业务描述都会一起删掉。',
                                okText: '删除',
                                okType: 'danger',
                                cancelText: '取消',
                                onOk: async () => {
                                  trackDialogClick(
                                    'formFiller',
                                    'confirm_delete_template',
                                    'confirm',
                                    {
                                      requirement_id: 1103,
                                      track_sign:
                                        'aai.b8134.click.ai_dialog_click_web_ai_form_filler'
                                    }
                                  )
                                  try {
                                    await xheyAi.deleteUserSkill(sk.name)
                                    message.success(
                                      `已删除「${sk.display_name || sk.name}」`
                                    )
                                    loadSkills()
                                  } catch (e) {
                                    message.error(
                                      `删除失败: ${
                                        e instanceof Error
                                          ? e.message
                                          : String(e)
                                      }`
                                    )
                                  }
                                },
                                onCancel: () => {
                                  trackDialogClick(
                                    'formFiller',
                                    'confirm_delete_template',
                                    'cancel',
                                    {
                                      requirement_id: 1103,
                                      track_sign:
                                        'aai.b8134.click.ai_dialog_click_web_ai_form_filler'
                                    }
                                  )
                                }
                              })
                            }}
                          >
                            <DeleteOutlined />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </section>
              )}

              {recentFiles.length > 0 && (
                <section className="aiClerk-homeV3__panel">
                  <div className="aiClerk-homeV3__panel-head">
                    <span className="aiClerk-homeV3__panel-bar" />
                    <h2 className="aiClerk-homeV3__panel-title">
                      最近填好的文档
                    </h2>
                  </div>
                  <div className="aiClerk-homeV3__recent-list">
                    {recentFiles.map((f) => {
                      // 界面展示用 original_name(AI/用户命名的显示名);老数据没这个字段
                      // 时回落到 filename(物理名),avoid 一直显示 xxx_1751_..._wid.xlsx
                      const displayName = f.original_name || f.filename
                      const iconStyle = pickFileIconStyle(displayName)
                      const inner = (
                        <>
                          <span
                            className="aiClerk-homeV3__recent-icon"
                            style={{
                              background: iconStyle.bg,
                              color: iconStyle.color
                            }}
                          >
                            <FileTypeIcon name={iconStyle.type} />
                          </span>
                          <div className="aiClerk-homeV3__recent-main">
                            <div className="aiClerk-homeV3__recent-name-row">
                              <span
                                className="aiClerk-homeV3__recent-name"
                                title={displayName}
                              >
                                {displayName}
                              </span>
                              {/* 行内改名:workingId 存在才渲染。按钮阻止事件冒泡,
                                  避免触发外层 <a> 的下载;Popover 内的表单是 portal
                                  到 body 的,天然不会误触发下载 */}
                              {f.working_id && (
                                <Popover
                                  open={renamingPath === f.path}
                                  onOpenChange={(o) =>
                                    o
                                      ? openRecentRename(f)
                                      : closeRecentRename()
                                  }
                                  trigger="click"
                                  placement="bottomLeft"
                                  destroyTooltipOnHide
                                  overlayClassName="aiClerk-homeV3__rename-pop"
                                  content={
                                    <div className="aiClerk-homeV3__rename-form">
                                      <Input
                                        size="small"
                                        autoFocus
                                        value={renameInput}
                                        onChange={(e) =>
                                          setRenameInput(e.target.value)
                                        }
                                        onPressEnter={() =>
                                          submitRecentRename(f)
                                        }
                                        onClick={(e) => e.stopPropagation()}
                                        disabled={renameBusy}
                                        placeholder="新的文件名(扩展名自动保留)"
                                        maxLength={80}
                                      />
                                      <div className="aiClerk-homeV3__rename-actions">
                                        <Button
                                          size="small"
                                          onClick={(e) => {
                                            e.stopPropagation()
                                            closeRecentRename()
                                          }}
                                          disabled={renameBusy}
                                        >
                                          取消
                                        </Button>
                                        <Button
                                          size="small"
                                          type="primary"
                                          onClick={(e) => {
                                            e.stopPropagation()
                                            submitRecentRename(f)
                                          }}
                                          loading={renameBusy}
                                        >
                                          确定
                                        </Button>
                                      </div>
                                    </div>
                                  }
                                >
                                  <button
                                    type="button"
                                    className="aiClerk-homeV3__recent-rename"
                                    onClick={(e) => {
                                      e.preventDefault()
                                      e.stopPropagation()
                                    }}
                                    title="重命名"
                                    aria-label="重命名"
                                  >
                                    <EditOutlined />
                                  </button>
                                </Popover>
                              )}
                            </div>
                            <div className="aiClerk-homeV3__recent-meta">
                              {[
                                formatSessionTime(f.mtime_ms),
                                formatBytes(f.size_bytes)
                              ]
                                .filter(Boolean)
                                .join(' · ')}
                            </div>
                          </div>
                          <span className="aiClerk-homeV3__recent-action">
                            {f.download_url ? (
                              <DownloadOutlined />
                            ) : (
                              <span className="aiClerk-homeV3__recent-disabled">
                                链接缺失
                              </span>
                            )}
                          </span>
                        </>
                      )
                      if (!f.download_url) {
                        return (
                          <div
                            key={f.path}
                            className="aiClerk-homeV3__recent is-disabled"
                          >
                            {inner}
                          </div>
                        )
                      }
                      if (!canExportShare) {
                        return (
                          <div
                            key={f.path}
                            className="aiClerk-homeV3__recent"
                            onClick={() => {
                              trackClick('formFiller', 'download_doc_blocked', {
                                requirement_id: 1103,
                                track_sign:
                                  'aai.b8134.click.ai_click_web_ai_form_filler',
                                string1: f.path
                              })
                              payment.openVipByKind(
                                CONFIG.VIP_KIND.VVIP,
                                'aiFormFiller',
                                false,
                                aiClerkPayContent({
                                  isVip: vipKind > 0,
                                  vipKind,
                                  usedPoints: credits?.consumed ?? 0
                                })
                              )
                            }}
                          >
                            {inner}
                          </div>
                        )
                      }
                      return (
                        <a
                          key={f.path}
                          href={f.download_url}
                          download={f.filename}
                          className="aiClerk-homeV3__recent"
                          onClick={() =>
                            trackClick('formFiller', 'download_doc', {
                              requirement_id: 1103,
                              track_sign:
                                'aai.b8134.click.ai_click_web_ai_form_filler',
                              string1: f.path
                            })
                          }
                        >
                          {inner}
                        </a>
                      )
                    })}
                  </div>
                </section>
              )}
            </div>
          ) : !isLoading ? (
            <section className="aiClerk-homeV3__guide">
              {/* ── Sec1 Hero ── */}
              <div className="aiClerk-homeV3__pf-hero">
                <h1 className="aiClerk-homeV3__pf-h1">
                  把你的照片和数据
                  <em className="aiClerk-homeV3__pf-brand">
                    自动填表、写汇报、做拼图
                  </em>
                </h1>
                <p className="aiClerk-homeV3__pf-sub">
                  支持 Excel 表格、Word 汇报、照片拼图——AI
                  读懂每一列、每一段要什么，从今日水印相机的照片和数据里取对内容，填得准、对得上。没有现成模板？描述一句也能生成。
                </p>
                <div className="aiClerk-homeV3__pf-btns">
                  <button
                    type="button"
                    className="aiClerk-homeV3__pf-btn-primary"
                    onClick={handlePickTemplate}
                    disabled={uploading}
                  >
                    <UploadOutlined />
                    上传表格，试填一份
                  </button>
                  <button
                    type="button"
                    className="aiClerk-homeV3__pf-btn-ghost"
                    onClick={handleWatchDemo}
                  >
                    <PlayCircleOutlined />看 AI 怎么填（30 秒）
                  </button>
                </div>
                <div className="aiClerk-homeV3__pf-trust">
                  <span className="aiClerk-homeV3__pf-avatars">
                    {[0, 1, 2].map((i) => {
                      const idx = (avatarOffset + i) % TRUST_NAMES.length
                      return (
                        <span
                          key={i}
                          style={{
                            background: TRUST_COLORS[idx % TRUST_COLORS.length]
                          }}
                        >
                          {TRUST_NAMES[idx]}
                        </span>
                      )
                    })}
                  </span>
                  2 万+ 团队在用 · 平均省 80% 填表时间
                </div>

                <HeroCarousel />
              </div>

              {/* ── Sec2 四步 + 信任 pill（前移：消解「不知道从哪开始」的第一焦虑）── */}
              <div className="aiClerk-homeV3__pf-sec aiClerk-homeV3__pf-sec--gray">
                <div className="aiClerk-homeV3__pf-sec-head">
                  <span className="aiClerk-homeV3__pf-eyebrow">四步搞定</span>
                  <h2 className="aiClerk-homeV3__pf-h2">
                    从上传到交付，最快几分钟
                  </h2>
                </div>
                <div className="aiClerk-homeV3__pf-steps">
                  {PF_STEPS.map((s, i) => (
                    <div key={s.title} className="aiClerk-homeV3__pf-step">
                      <span className="aiClerk-homeV3__pf-step-node">
                        {i + 1}
                      </span>
                      <span className="aiClerk-homeV3__pf-step-ic">
                        <s.icon />
                      </span>
                      <div className="aiClerk-homeV3__pf-step-text">
                        <div className="aiClerk-homeV3__pf-step-title">
                          {s.title}
                        </div>
                        <div className="aiClerk-homeV3__pf-step-desc">
                          {s.desc}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="aiClerk-homeV3__pf-trustpill">
                  <SafetyOutlined />
                  AI
                  不瞎填，会先跟你确认每一张照片、人名和时间——确认了才落到成品里
                </div>
              </div>

              {/* ── Sec3 数据映射 + 表单库 ── */}
              <div className="aiClerk-homeV3__pf-sec">
                <div className="aiClerk-homeV3__pf-sec-head">
                  <span className="aiClerk-homeV3__pf-eyebrow">
                    一份数据，填进任意表
                  </span>
                  <h2 className="aiClerk-homeV3__pf-h2">
                    你拍下的每个字段，AI 自动填进表格、写进汇报
                  </h2>
                  <p className="aiClerk-homeV3__pf-sub2">
                    时间、地点、经纬度、真实海拔、天气、作业人员、水印名称……今日水印相机记录的字段，AI
                    自动填进 Excel、写进 Word 汇报、排进照片拼图，不挑模板。
                  </p>
                </div>

                {/* 数据映射:照片字段 → AI 匹配 → 表格(竖排适配窄栏) */}
                <div className="aiClerk-homeV3__pf-map">
                  <div className="aiClerk-homeV3__pf-mapcard">
                    <div className="aiClerk-homeV3__pf-mapcard-head">
                      <span>
                        <CameraOutlined /> 照片自带数据
                      </span>
                      <em>来自团队相册</em>
                    </div>
                    <div
                      className="aiClerk-homeV3__pf-thumb"
                      style={{ backgroundImage: `url(${dataSamplePhoto})` }}
                    >
                      <span className="aiClerk-homeV3__pf-thumb-wm">
                        真实海拔 576.4m · 18:52
                      </span>
                    </div>
                    <div className="aiClerk-homeV3__pf-fields">
                      {PF_PHOTO_FIELDS.map((f) => (
                        <span key={f} className="aiClerk-homeV3__pf-field">
                          <i className="aiClerk-homeV3__pf-field-dot" />
                          {f}
                        </span>
                      ))}
                    </div>
                  </div>
                  <div className="aiClerk-homeV3__pf-mapconn">
                    <span className="aiClerk-homeV3__pf-mapconn-line" />
                    <span className="aiClerk-homeV3__pf-mapai">
                      <StarFilled /> AI 自动对应 · 5 列已匹配
                    </span>
                    <DownOutlined className="aiClerk-homeV3__pf-mapconn-arrow" />
                  </div>
                  <div className="aiClerk-homeV3__pf-ledger">
                    <div className="aiClerk-homeV3__pf-ledger-title">
                      <b>现场作业影像台账 · 2026-04</b>
                      <span>86 条</span>
                    </div>
                    <div className="aiClerk-homeV3__pf-sheet">
                      <div className="aiClerk-homeV3__pf-sheet-row aiClerk-homeV3__pf-sheet-row--head">
                        {PF_LEDGER_HEADERS.map((h) => (
                          <span key={h}>{h}</span>
                        ))}
                      </div>
                      {PF_LEDGER_ROWS.map((row, r) => (
                        <div key={r} className="aiClerk-homeV3__pf-sheet-row">
                          {row.map((v, c) => (
                            <span
                              key={c}
                              className={
                                c === 2
                                  ? 'aiClerk-homeV3__pf-sheet-cell--alt'
                                  : undefined
                              }
                            >
                              {v}
                            </span>
                          ))}
                          <span className="aiClerk-homeV3__pf-sheet-photo" />
                        </div>
                      ))}
                    </div>
                    <div className="aiClerk-homeV3__pf-ledger-foot">
                      <CheckCircleFilled /> 5 列已自动匹配 ·
                      每格都对位到正确的照片
                    </div>
                  </div>
                </div>

                {/* 表单库:一份数据自动适配的行业模板(分类标签墙) */}
                <SupportedTypes onPick={handleQuickFill} />
              </div>

              {/* ── Sec3 协作 + 对话(2 特性,各带 3 勾选) ── */}
              <div className="aiClerk-homeV3__pf-sec">
                {PF_FEATURES.map((f, i) => (
                  <div key={f.title} className="aiClerk-homeV3__pf-feature">
                    <div className="aiClerk-homeV3__pf-feature-txt">
                      <span
                        className="aiClerk-homeV3__pf-eyebrow"
                        style={{ color: f.accent, background: f.accentBg }}
                      >
                        {f.eyebrow}
                      </span>
                      <h2 className="aiClerk-homeV3__pf-h2">{f.title}</h2>
                      <p className="aiClerk-homeV3__pf-sub2">{f.desc}</p>
                      <div className="aiClerk-homeV3__pf-checks">
                        {f.checks.map((c) => (
                          <div key={c} className="aiClerk-homeV3__pf-check">
                            <span
                              className="aiClerk-homeV3__pf-check-ic"
                              style={{
                                color: f.accent,
                                background: f.accentBg
                              }}
                            >
                              <CheckOutlined />
                            </span>
                            {c}
                          </div>
                        ))}
                      </div>
                    </div>
                    <div className="aiClerk-homeV3__pf-feature-vis">
                      {i === 0 ? (
                        <>
                          <span className="aiClerk-homeV3__pf-fv-label">
                            从照片库挑对的那张
                          </span>
                          <div className="aiClerk-homeV3__pf-fv-thumbs">
                            <span className="aiClerk-homeV3__pf-fv-thumb">
                              <PictureOutlined />
                            </span>
                            <span className="aiClerk-homeV3__pf-fv-thumb aiClerk-homeV3__pf-fv-thumb--on">
                              <PictureOutlined />
                            </span>
                            <span className="aiClerk-homeV3__pf-fv-thumb">
                              <PictureOutlined />
                            </span>
                          </div>
                          <DownOutlined className="aiClerk-homeV3__pf-fv-arrow" />
                          <span className="aiClerk-homeV3__pf-fv-cell">
                            <CheckOutlined /> 已填进本格
                          </span>
                        </>
                      ) : (
                        <>
                          <span className="aiClerk-homeV3__pf-fv-srccell">
                            <PictureOutlined /> B3 单元格
                          </span>
                          <span className="aiClerk-homeV3__pf-fv-bubble">
                            换成下午那张
                          </span>
                          <span className="aiClerk-homeV3__pf-fv-done">
                            <CheckOutlined /> 已重填
                          </span>
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {/* ── Sec3.5 预览 / 编辑页能力(功能介绍) ── */}
              <div className="aiClerk-homeV3__pf-sec aiClerk-homeV3__pf-sec--gray">
                <div className="aiClerk-homeV3__pf-sec-head">
                  <span className="aiClerk-homeV3__pf-eyebrow">不止于填</span>
                  <h2 className="aiClerk-homeV3__pf-h2">
                    填完，在预览区直接改
                  </h2>
                  <p className="aiClerk-homeV3__pf-sub2">
                    成品只是开始——在预览区换图、增删行列、改写段落、调美观，跟
                    AI 说一句，它帮你改好。
                  </p>
                </div>

                {/* 预览区编辑窗 mock:工具栏 + 迷你表 + 对话框,呼应「在预览区直接改」 */}
                <div className="aiClerk-homeV3__pf-editmock">
                  <div className="aiClerk-homeV3__pf-em-bar">
                    <div className="aiClerk-homeV3__pf-em-barleft">
                      <span className="aiClerk-homeV3__pf-em-dot aiClerk-homeV3__pf-em-dot--r" />
                      <span className="aiClerk-homeV3__pf-em-dot aiClerk-homeV3__pf-em-dot--y" />
                      <span className="aiClerk-homeV3__pf-em-dot aiClerk-homeV3__pf-em-dot--g" />
                      <span className="aiClerk-homeV3__pf-em-file">
                        巡检台账.xlsx
                      </span>
                    </div>
                  </div>

                  <div className="aiClerk-homeV3__pf-em-table">
                    <div className="aiClerk-homeV3__pf-em-row aiClerk-homeV3__pf-em-row--head">
                      <span className="aiClerk-homeV3__pf-em-cell aiClerk-homeV3__pf-em-cell--pt">
                        点位
                      </span>
                      <span className="aiClerk-homeV3__pf-em-cell aiClerk-homeV3__pf-em-cell--st aiClerk-homeV3__pf-em-cell--hl">
                        状态
                      </span>
                      <span className="aiClerk-homeV3__pf-em-cell aiClerk-homeV3__pf-em-cell--ph">
                        照片
                      </span>
                    </div>
                    {[
                      ['1# 主变区', '正常'],
                      ['2# 母线桥', '正常'],
                      ['3# 开关柜', '异常'],
                      ['4# 配电室', '正常'],
                      ['5# 主控室', '正常']
                    ].map((row) => (
                      <div key={row[0]} className="aiClerk-homeV3__pf-em-row">
                        <span className="aiClerk-homeV3__pf-em-cell aiClerk-homeV3__pf-em-cell--pt">
                          {row[0]}
                        </span>
                        <span
                          className={`aiClerk-homeV3__pf-em-cell aiClerk-homeV3__pf-em-cell--st${
                            row[1] === '异常'
                              ? ' aiClerk-homeV3__pf-em-cell--err'
                              : ' aiClerk-homeV3__pf-em-cell--alt'
                          }`}
                        >
                          {row[1]}
                        </span>
                        <span className="aiClerk-homeV3__pf-em-cell aiClerk-homeV3__pf-em-cell--ph">
                          <span className="aiClerk-homeV3__pf-em-photo">
                            <PictureOutlined />
                          </span>
                        </span>
                      </div>
                    ))}
                  </div>

                  <div className="aiClerk-homeV3__pf-em-chat">
                    <span className="aiClerk-homeV3__pf-em-ph">
                      把「状态」这列都填上，再把表头调成蓝底…
                    </span>
                  </div>
                </div>

                <div className="aiClerk-homeV3__pf-caps">
                  {PF_CAPS.map((c) => (
                    <div key={c.title} className="aiClerk-homeV3__pf-cap">
                      <span
                        className="aiClerk-homeV3__pf-cap-ic"
                        style={{ color: c.color, background: c.bg }}
                      >
                        <c.icon />
                      </span>
                      <div className="aiClerk-homeV3__pf-cap-text">
                        <div className="aiClerk-homeV3__pf-cap-title">
                          {c.title}
                        </div>
                        <div className="aiClerk-homeV3__pf-cap-desc">
                          {c.desc}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* ── Sec6 收尾 CTA ── */}
              <div className="aiClerk-homeV3__pf-cta">
                <span className="aiClerk-homeV3__pf-cta-badge">
                  <CheckCircleFilled /> 填得准 · 写得好 · 改得动
                </span>
                <h2 className="aiClerk-homeV3__pf-cta-title">
                  把第一张表，交给 AI
                </h2>
                <p className="aiClerk-homeV3__pf-cta-sub">
                  上传 Excel / Word 模板，AI
                  帮你填好、写好；没有模板，描述一句也能生成。
                </p>
                <div className="aiClerk-homeV3__pf-cta-btns">
                  <button
                    type="button"
                    className="aiClerk-homeV3__pf-cta-primary"
                    onClick={handlePickTemplate}
                    disabled={uploading}
                  >
                    <UploadOutlined /> 上传表格，试填一份
                  </button>
                  <button
                    type="button"
                    className="aiClerk-homeV3__pf-cta-ghost"
                    onClick={handleDescribeCta}
                  >
                    <MessageOutlined /> 描述需求生成
                  </button>
                </div>
                <div className="aiClerk-homeV3__pf-cta-risk">
                  <SafetyOutlined />
                  你的照片和数据不外泄、不用于训练
                </div>
              </div>
            </section>
          ) : null}
        </div>

        {/* hover 快捷入口 → 右侧浮出该模板大预览(仅 hover 时;纯展示不拦鼠标)。
            CHIP_PREVIEW_BASE 未配 → 走骨架占位,配好 OSS 地址后自动显示真实预览图。 */}
        {hoveredChip &&
          (() => {
            const previewUrl = getChipPreviewUrl(hoveredChip.label)
            return (
              <div
                className="aiClerk-homeV3__chip-preview-panel"
                aria-hidden="true"
              >
                <div className="aiClerk-homeV3__chip-preview-title">
                  {hoveredChip.label}
                  <span className="aiClerk-homeV3__chip-preview-tag">
                    模板预览
                  </span>
                </div>
                <div className="aiClerk-homeV3__chip-preview-frame">
                  {previewUrl ? (
                    <img
                      className="aiClerk-homeV3__chip-preview-img"
                      src={previewUrl}
                      alt={`${hoveredChip.label} 模板预览`}
                    />
                  ) : (
                    <div className="aiClerk-homeV3__chip-preview-ph">
                      <div className="aiClerk-homeV3__chip-preview-ph-bar" />
                      <div className="aiClerk-homeV3__chip-preview-ph-line" />
                      <div className="aiClerk-homeV3__chip-preview-ph-line" />
                      <div className="aiClerk-homeV3__chip-preview-ph-line" />
                      <span className="aiClerk-homeV3__chip-preview-ph-hint">
                        预览图待上传
                      </span>
                    </div>
                  )}
                </div>

                {/* 模板使用说明:讲清工作机制,重点突出「照片数据自动写入文档」 */}
                <div className="aiClerk-homeV3__chip-preview-guide">
                  <div className="aiClerk-homeV3__chip-preview-guide-lead">
                    <span className="aiClerk-homeV3__chip-preview-guide-hl">
                      照片数据自动写入文档
                    </span>
                    ——你只管拍照，AI
                    读出照片里的时间、地点、内容，按行填进这份模板
                  </div>
                  <div className="aiClerk-homeV3__chip-preview-steps">
                    {PREVIEW_FLOW.map((s, i) => (
                      <div
                        key={s.n}
                        className={`aiClerk-homeV3__chip-preview-step${
                          i === 2
                            ? ' aiClerk-homeV3__chip-preview-step--key'
                            : ''
                        }`}
                      >
                        <div className="aiClerk-homeV3__chip-preview-step-head">
                          <span className="aiClerk-homeV3__chip-preview-step-n">
                            {s.n}
                          </span>
                          <span className="aiClerk-homeV3__chip-preview-step-t">
                            {s.title}
                          </span>
                        </div>
                        <div className="aiClerk-homeV3__chip-preview-step-d">
                          {s.desc}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )
          })()}
      </div>

      {/* chip 点击后的时间范围选择弹窗(独立组件,portal 到 body) */}
      <ChipTimePicker
        open={!!pendingChip}
        subtitle="数据统计的时间范围"
        title={
          pendingChip
            ? `「${pendingChip.label}」用哪段时间的数据?`
            : '选择时间范围'
        }
        busy={uploading}
        onClose={closeChipTimePicker}
        onConfirm={confirmChipTime}
      />

      {/* ── 上传模板后弹窗(与 V1/V2 一致,复用 index.scss 的 start-modal 样式) ── */}
      <Modal
        title={null}
        open={startModalOpen}
        onCancel={handleStartModalClose}
        mask={{ closable: !uploading }}
        closable={!uploading}
        footer={null}
        width={720}
        destroyOnHidden
        zIndex={790}
        wrapClassName="aiClerk-home__start-modal-wrap"
      >
        <div className="aiClerk-home__start-modal-head">
          <h2 className="aiClerk-home__start-modal-h2">上传文档</h2>
        </div>

        <div className="aiClerk-home__start-modal-block">
          <div className="aiClerk-home__start-modal-block-label">上传文档</div>
          {templateFile ? (
            <div className="aiClerk-home__start-modal-file-card">
              <div className="aiClerk-home__start-modal-file-icon-wrap">
                <FileTypeIcon
                  name={
                    /\.(docx|doc)$/i.test(templateFile.name)
                      ? 'file-word'
                      : /\.(pptx|ppt)$/i.test(templateFile.name)
                        ? 'file-ppt'
                        : 'file-excel'
                  }
                  className="aiClerk-home__start-modal-file-icon"
                />
              </div>
              <div className="aiClerk-home__start-modal-file-info">
                <div
                  className="aiClerk-home__start-modal-file-name"
                  title={templateFile.name}
                >
                  {templateFile.name}
                </div>
                <div className="aiClerk-home__start-modal-file-meta">
                  <span>{(templateFile.size / 1024).toFixed(0)} KB</span>
                </div>
              </div>
              <button
                type="button"
                className="aiClerk-home__start-modal-file-close"
                onClick={handleClearTemplateInModal}
                aria-label="移除模板"
                disabled={uploading}
              >
                ×
              </button>
            </div>
          ) : (
            <div className="aiClerk-home__start-modal-sample-card aiClerk-home__start-modal-sample-card--empty">
              <span className="aiClerk-home__start-modal-sample-icon">
                <ArrowUpOutlined />
              </span>
              <div className="aiClerk-home__start-modal-sample-text">
                <div className="aiClerk-home__start-modal-sample-title">
                  上传一份要填的文档
                </div>
                <div className="aiClerk-home__start-modal-sample-desc">
                  支持{' '}
                  <span className="aiClerk-home__start-modal-ext">.docx</span>{' '}
                  <span className="aiClerk-home__start-modal-ext">.xlsx</span>{' '}
                  格式
                </div>
              </div>
              <Button
                type="primary"
                className="aiClerk-home__start-modal-sample-btn"
                onClick={handlePickTemplate}
                disabled={uploading}
              >
                上传文档
              </Button>
            </div>
          )}
        </div>

        <div className="aiClerk-home__start-modal-block">
          <div className="aiClerk-home__start-modal-block-label">
            也可上传
            <strong className="aiClerk-home__start-modal-block-label-em">
              【样例】
            </strong>
            提高填写准确率
            <span className="aiClerk-home__start-modal-block-label-tag">
              可选
            </span>
          </div>

          {sampleFile ? (
            <div
              className={`aiClerk-home__start-modal-sample-card aiClerk-home__start-modal-sample-card--filled${
                dragOverSample ? ' is-drag-over' : ''
              }`}
              onDragOver={handleSampleDragOver}
              onDragLeave={handleSampleDragLeave}
              onDrop={handleSampleDrop}
            >
              <div className="aiClerk-home__start-modal-file-icon-wrap">
                <FileTypeIcon
                  name={
                    /\.(docx|doc)$/i.test(sampleFile.name)
                      ? 'file-word'
                      : 'file-excel'
                  }
                  className="aiClerk-home__start-modal-file-icon"
                />
              </div>
              <div className="aiClerk-home__start-modal-file-info">
                <div
                  className="aiClerk-home__start-modal-file-name"
                  title={sampleFile.name}
                >
                  {sampleFile.name}
                </div>
                <div className="aiClerk-home__start-modal-file-meta">
                  {(sampleFile.size / 1024).toFixed(0)} KB
                </div>
              </div>
              <Button
                type="primary"
                className="aiClerk-home__start-modal-sample-btn"
                onClick={handlePickSample}
                disabled={uploading}
              >
                上传样例
              </Button>
            </div>
          ) : (
            <div
              className={`aiClerk-home__start-modal-sample-card aiClerk-home__start-modal-sample-card--empty${
                dragOverSample ? ' is-drag-over' : ''
              }`}
              onDragOver={handleSampleDragOver}
              onDragLeave={handleSampleDragLeave}
              onDrop={handleSampleDrop}
            >
              <span className="aiClerk-home__start-modal-sample-icon">
                <ThunderboltFilled />
              </span>
              <div className="aiClerk-home__start-modal-sample-text">
                <div className="aiClerk-home__start-modal-sample-title">
                  上传样例,让识别更准
                </div>
                <div className="aiClerk-home__start-modal-sample-desc">
                  再传一份「之前填好的」样例,AI 会对比学习你的填写习惯,
                  提高填写正确率
                </div>
              </div>
              <Button
                type="primary"
                className="aiClerk-home__start-modal-sample-btn"
                onClick={handlePickSample}
                disabled={uploading}
              >
                上传样例
              </Button>
            </div>
          )}
        </div>

        {(probing || probeResult) && (
          <div className="aiClerk-home__probe" style={{ marginTop: 12 }}>
            {probing && (
              <div className="aiClerk-home__probe-pill aiClerk-home__probe-pill--neutral">
                <LoadingOutlined /> 正在检测样例与模板的字段匹配…
              </div>
            )}
            {!probing && probeResult && !probeResult.parseError && (
              <>
                {probeResult.matchPercent >= 70 && (
                  <div className="aiClerk-home__probe-pill aiClerk-home__probe-pill--ok">
                    <CheckCircleOutlined /> 字段匹配良好(
                    {probeResult.matchedHeaders}/{probeResult.totalHeaders}{' '}
                    列同名)
                  </div>
                )}
                {probeResult.matchPercent >= 30 &&
                  probeResult.matchPercent < 70 && (
                    <div className="aiClerk-home__probe-pill aiClerk-home__probe-pill--warn">
                      <WarningOutlined /> 仅 {probeResult.matchPercent}%
                      列对得上(
                      {probeResult.matchedHeaders}/{probeResult.totalHeaders}
                      ),确认是同类型表?
                    </div>
                  )}
                {probeResult.matchPercent < 30 &&
                  probeResult.totalHeaders > 0 && (
                    <div className="aiClerk-home__probe-pill aiClerk-home__probe-pill--err">
                      <CloseCircleOutlined /> 看起来不是同类型表(
                      {probeResult.matchedHeaders}/{probeResult.totalHeaders}{' '}
                      列匹配)
                    </div>
                  )}
                {probeResult.reversed && (
                  <div className="aiClerk-home__probe-pill aiClerk-home__probe-pill--info">
                    <SwapOutlined /> 模板槽里的文件填得更满,是不是放反了?
                    <button
                      type="button"
                      className="aiClerk-home__probe-swap"
                      onClick={handleSwapTemplateAndSample}
                      disabled={uploading}
                    >
                      一键互换
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        <input
          ref={sampleInputRef}
          type="file"
          accept={ACCEPT_TYPES}
          hidden
          onChange={handleSampleChange}
        />

        <div className="aiClerk-home__start-modal-footer">
          <Button
            type="primary"
            size="large"
            className="aiClerk-home__start-btn aiClerk-home__start-modal-cta"
            onClick={() => handleStart()}
            disabled={!templateFile || uploading}
            loading={uploading}
          >
            {uploading ? '上传中…' : '开始识别'}
          </Button>
        </div>
      </Modal>

      {/* ── 积分不足拦截弹窗 + 增购积分包 ── */}
      <CreditsExhaustedModal
        visible={creditsModalVisible}
        onClose={() => setCreditsModalVisible(false)}
        available={credits?.available ?? 0}
        cost={SINGLE_QUERY_COST}
        vipKind={vipKind}
        onBuyPack={() => {
          setCreditsModalVisible(false)
          setStartModalOpen(false)
          setPointsPackVisible(true)
        }}
        onUpgrade={() => {
          setCreditsModalVisible(false)
          setStartModalOpen(false)
          payment.openVipByKind(
            vipKind || CONFIG.VIP_KIND.VIP,
            'aiFormFiller',
            false,
            aiClerkPayContent({
              isVip: vipKind > 0,
              vipKind,
              usedPoints: credits?.consumed ?? 0
            })
          )
        }}
      />
      <PointsPackModal
        visible={pointsPackVisible}
        onClose={() => setPointsPackVisible(false)}
        source="aiFormFillerBuyCredits"
        onPaymentSuccess={() => {
          setPointsPackVisible(false)
          refreshCredits()
          runPendingStart()
        }}
      />
      <EditSkillModal
        skill={editingSkill}
        open={!!editingSkill}
        onClose={() => setEditingSkill(null)}
        onSaved={() => {
          setEditingSkill(null)
          loadSkills()
        }}
      />
      <DemoReplayModal
        visible={demoVisible}
        onClose={() => setDemoVisible(false)}
      />

      <GiftModal
        visible={giftOpen}
        onClose={() => {
          trackDialogClick('formFiller', 'gift_modal', 'close', {
            requirement_id: 1103,
            track_sign: 'aai.b8134.click.ai_dialog_click_web_ai_form_filler'
          })
          setGiftOpen(false)
        }}
        onLater={() => {
          trackDialogClick('formFiller', 'gift_modal', 'later', {
            requirement_id: 1103,
            track_sign: 'aai.b8134.click.ai_dialog_click_web_ai_form_filler'
          })
          setGiftOpen(false)
        }}
        onStart={() => {
          trackDialogClick('formFiller', 'gift_modal', 'start', {
            requirement_id: 1103,
            track_sign: 'aai.b8134.click.ai_dialog_click_web_ai_form_filler'
          })
          setGiftOpen(false)
          handlePickTemplate()
        }}
      />
    </div>
  )
}

export default HomeV3
